import { UsMetricHeaders } from '../ValueDashboard/us/UsMarketMetrics';
import { usPlayerPanelGame } from '@/views/ValueDashboard/us/usPlayerPanelGame';
import { lineLadderReference } from './lineLadderReference';
// ── GameLinesSection — period "Game Lines" table + MLB Props/Events tabs ──
// A SEPARATE, additive section on the Game Detail page; the full-game
// Moneyline / Spread / Total sections are untouched. Renders period bets
// through the SAME GameRows component the moneyline table uses — identical
// columns + paired LadderRow orderbook ladder on expand — just with the Time
// (Score) column hidden. Layout: tabs + section toolbar → bet-type segmented
// tabs → period segmented tabs → column header → GameRows.
//
// MLB three-tab selector (mlb-props-events, 2026-07-14): on MLB games the
// static "Game Lines" title becomes a Game Lines | Player Props | Events
// selector. Player Props = stat chips (Hits … H+R+RBI) → per-player yes-ladder
// rows; Events = YRFI/NRFI, per-team Team Total ladders, Extra Innings. Both
// ride the same GameRows synth-row path as BTTS. Since poly-props-events
// (2026-07-16) the P columns fill for the venues Poly lists: HR/Ks prop
// ladders (joined client-side by marketStore — see getMlbPlayerProps),
// YRFI (Poly `-nrfi`, SAME polarity — no flip) and Extra Innings. The other
// six stats + team totals stay Kalshi-only ("—" in the P columns, the
// quarters/F5 precedent). Poly PROP books are static Gamma snapshots
// (~120s) with NO orderbook — never subscribed for depth here; the
// RFI/extras binaries DO carry live WS books and are subscribed like any
// game line. Tabs with no ingested markets render disabled — expected until
// the feed side ships and, for props, until lineups post (~game time).
// Game Lines tab behavior is unchanged; other sports keep the static title.
//
// Periods: 1st Half (Kalshi + Poly) + single quarters 1Q–4Q (Kalshi-only —
// Polymarket lists no quarter markets, so quarter rows show "—" for Poly; that
// is expected, not a bug), plus MLB First 5 Innings (F5, Kalshi-only — same "—"
// Poly column). The period tabs are data-driven (enabled only where the active
// bet type has markets). Moneyline runs through the 3-way (Away/Home/Tie) path
// for 1H / 2H / F5 — there is no quarter winner — so quarter tabs stay disabled
// on the Moneyline sub-tab.
//
// Data: getAvailablePeriods (which tabs to enable), getGameEvents(…, period)
// for spread/total, getPeriodMoneyline('1H') for the 3-way (Away/Home/Tie)
// winner. Period markets share the full-game canonicalGameKey but are bucketed
// by period, so the full-game rows are never affected (the 0cf7549 invariant).
// A quarter resolves on that quarter alone — 2Q ≠ 1H — never paired across.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { useMarketStore, TERMINAL_STATUSES, useOpOddsFor } from '@/stores/marketStore';
import { useAccountStore } from '@/stores/accountStore';
import { requestPropsFeed } from '@/hooks/useHydra';
import { useDisplaySettings } from '@/stores/displaySettings';
import { opHeaderTooltip, opSliceForSport, OP_HEADER_LABEL, OP_PERIOD_WIRE } from '@/lib/opOdds';
import { Th } from '@/components/atoms/TableCells';
import { usSpreadCell, usTotalCell } from '../SpreadsTotals/usLineCell';
import { usGameKeyForBoardKey } from '@/lib/polymarketUs/gameMapping';
import { DISARM_REASON, disarmIfArmed } from '../ValueDashboard/oneClickDisarmReasons';
import { GameRows, OpOddsText } from '@/views/ValueDashboard/GameTable';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from '@/views/ValueDashboard/sharedBookCue';
import { SectionToolbar, Chip, ChipGroup } from './SectionToolbar';
import { buildCfbEventSynth, cfbEnumRowLabel, cfbEventSynthKey } from './cfbEventRows';
import { buildPolyFightPropSynth, buildUfcFightPropSynth } from './ufcFightPropRows';
import { cfbEventGroupKey, cfbEventNoLabel } from '@/lib/sport/cfbEventSynthKey';
import { EdgeFadeScroller } from '@/components/cards/cardKit';
import {
  CompactLinesTable,
  GAME_DETAIL_TABLE_FADE,
  LineHoldStatus,
  closestKalshiYesTo50,
  spreadLineLabel,
  totalLineLabel,
  useLineListSettled,
  type LineHoldEntry,
} from './CompactLinesTable';
import { CompactOutcomeBands, type CompactOutcome } from './CompactOutcomeBands';
import {
  buildBandOutcomes,
  resolveFocusRungSelection,
  synthMatchesTicker,
  bandRowVisible,
} from './bandOutcomes';
import { buildLadderTableRows, chipKeyFor } from './ladderRows';
import { buildPlayerPropSummaries } from './playerPropSummary';
import { buildPlayerPropSynth } from './propEventRows';
import { propRowSeedOnToggle, resolveRungVenue } from './propSelection';
import { buildLadderBetLineContext } from './ladderBetLineContext';
import { MiniLineStrip } from '@/views/SpreadsTotals/MiniLineStrip';
import { buildSpreadBetRows, buildTotalBetRows } from './lineBetRows';
import {
  synthesizeFromSpread,
  synthesizeFromTotal,
  linePriceCents,
  resolveLineIndexByKey,
  spreadLineKey,
  totalLineKey,
} from '@/views/SpreadsTotals/synthesize';
import { buildBetLineContext } from '@/views/SpreadsTotals/buildBetLineContext';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import { classifyMlbExtendedTicker } from '@/lib/kalshiTicker/sports/mlb';
import { classifyPolyExtendedTicker } from '@/lib/kalshiTicker/polyPropsEvents';
import { isPropStatSport, propSportHasTwinSeries, propStatOfKalshiSeries, propStatSport, PROP_STAT_LABELS } from '@/lib/sport/propStat';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { WorkingViewReset } from '@/components/atoms/WorkingViewReset';
import { BET_TYPES, PERIODS, SECTION_TABS, gameLinesReading, readingInstance } from './readingView';
import { useGameDetailResumesLatest } from './recentViews';
import { BOTH_TEAMS_POINTS_LABEL } from '@/lib/cfbWireMarkets';
import type { ActiveSpreadTotal } from './SpreadTotalSection';
import type { CfbEventMarkets, UfcFightProps, PolyFightPropRow, GameRow, GameEventRow, MarketSide, MlbEventMarkets, OpBucketRef, Period, PlayerPropStatGroup, SpreadLine, TeamSide, TotalLine, TradeTicketContext, VenueBook } from '@/views/ValueDashboard/types';
import { tryOneClickFromTicketCtx } from '@/views/ValueDashboard/oneClickIntercept';
import { lt2PeriodFor } from '@/lib/trading/liveIntent';
import { lt2CardId } from '@/views/SpreadsTotals/SpreadsTotalsTable';
import { usePinLineOnAim } from './usePinLineOnAim';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useLt2Access } from '@/hooks/useLt2Access';
import { filterEventExecutableLines } from '@/views/SpreadsTotals/synthesize';
import { useFeedNeeds } from '@/hooks/useFeedNeeds';
import { altTokenFor } from '@/stores/marketFrameNormalization';
import { GAME_DETAIL_CLASSES } from '@/workers/feedFilter';
import { useUsPlayerCatalogue } from './usPlayerCatalogue';
import { UsGameProps } from './UsGameProps';
import { usGamePropTarget } from '@/lib/polymarketUs/gamePropTarget';
import { usGamePropPanelGame } from '@/views/ValueDashboard/us/usGamePropPanelGame';
import { UsPlayerProps } from './UsPlayerProps';
import { usPlayerTarget } from '@/lib/polymarketUs/playerTarget';

type BetType = 'spread' | 'total' | 'moneyline' | 'btts' | 'race' | 'overtime';

/** The UFC title for the 'events' tab (#848) — Kalshi's per-bout props. */
const UFC_FIGHT_PROPS_TAB_LABEL = 'Fight Props';

/** Top-level section tabs. 'props' = MLB + NFL player props (mlb-props-events,
 *  nfl-player-props); 'events' ("Game Props") = MLB, CFB and NFL game-event
 *  families; every other sport is pinned to 'lines' with the static title. */
type SectionTab = 'lines' | 'props' | 'events';

interface Props {
  /** Full-game GameRow (carries codes / names / sport). Period lines hang off
   *  the same canonicalGameKey (= game.key). */
  game: GameRow;
  /** Deep-link focus from the Positions page (`?ticker=&side=`). When the
   *  ticker is an NBA period market for this game, the bet-type + period tabs
   *  jump to it, the line expands, and the trade ticket seeds. Ignored
   *  otherwise (full-game tickers are handled by the other sections). */
  focusTicker?: string | null;
  /** #1238 — the page's reading focus (`gameDetailFocus`). Absent: the ticker. */
  readingFocus?: string | null;
  focusUsMarket?: string | null;
  focusSide?: 'yes' | 'no';
  /** Re-seeds the trade ticket. Same handler the full-game sections use. */
  onPillClick: (ctx: TradeTicketContext) => void;
  onUsPillClick?: (ctx: TradeTicketContext) => void;
  /** Emits the active spread/total line's bet-line context (synth game +
   *  "Over X" / "TEAM wins by over X" title + line strip) so GameDetail's
   *  right-side panel renders the same over-only/YES-NO layout the full-game
   *  SpreadTotalSection produces. Null on a moneyline selection. */
  onActiveLineChange?: (active: ActiveSpreadTotal | null) => void;
  /** Which spread/total line owns the panel — PARENT-controlled (mirrors
   *  SpreadTotalSection's activeKind) so the parent can release it when the
   *  middle table or a moneyline takes over the panel, stopping this section's
   *  emit from clobbering. Null = no active line / moneyline. */
  panelLine?: { kind: 'spread' | 'total'; key: string } | null;
  onPanelLineChange?: (line: { kind: 'spread' | 'total'; key: string } | null) => void;
}

/** One renderable bet = a synth GameRow + how GameRows should show it. */
// ── OpSpecialsBlock — full-feed B's OP-only families ────────────────
// Football totals specials and odd/even have no Kalshi/Poly counterpart
// rows, so they render as their own compact block under the Game Lines
// table — appearing only when the feed actually carries them (feature
// detection by presence, no versioning). Dan's display bar (2026-08-17):
// any market shown shows consensus + Pinnacle — OpOddsText is exactly
// that cell, so the bar is satisfied by construction; entries without a
// consensus never reach the wire (contract) and would render anchor-only
// if they somehow did.
// Team totals (`teamtotal-home` / `teamtotal-away`) left this block on
// 2026-09-06: they JOIN the Kalshi team-total ladders on the Game Props
// tab (opTeamTotalBucket below) and are no longer listed here. The two
// football totals stay pinned as-is — hydracore is opening the paired
// gate for them.
const OP_SPECIAL_FAMILIES: Record<string, { label: string; away: string; home: string }> = {
  'total-touchdowns': { label: 'Total Touchdowns', away: 'Over', home: 'Under' },
  'total-fieldgoals': { label: 'Total Field Goals', away: 'Over', home: 'Under' },
  oddeven: { label: 'Odd / Even', away: 'Odd', home: 'Even' },
  'oddeven-away': { label: 'Odd / Even — Away', away: 'Odd', home: 'Even' },
  'oddeven-home': { label: 'Odd / Even — Home', away: 'Odd', home: 'Even' },
};

export function OpSpecialsBlock({ gameKey }: { gameKey: string }) {
  const version = useMarketStore((st) => st.version);
  // Odds filter (DNL-001, round 1): OP-off hides the OP-only specials too —
  // preference gates BEFORE availability, hooks stay unconditional.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const slices = useMemo(() => {
    void version;
    if (!showOpOdds) return [];
    return useMarketStore
      .getState()
      .getOpMarketsFor(gameKey)
      .filter((sl) => OP_SPECIAL_FAMILIES[sl.market.marketType] != null);
  }, [gameKey, version, showOpOdds]);
  if (slices.length === 0) return null;
  return (
    <div className="border-border mt-2 border-t pt-2" data-testid="op-specials">
      <div
        className="text-muted-foreground px-2 pb-1 text-[11px] font-semibold"
        title="OddsPapi-only families (full-feed B) — de-vigged fair values, consensus / Pinnacle per side, hydra-computed. Comparison only; no venue books exist for these rows."
      >
        {OP_HEADER_LABEL} Specials
      </div>
      <table className="w-full border-collapse">
        <tbody>
          {slices.map((sl) => {
            const fam = OP_SPECIAL_FAMILIES[sl.market.marketType]!;
            const label = fam.label;
            const period =
              sl.market.periodTypeId === 1 ? '' : ` · ${sl.market.periodLabel}`;
            const line = sl.market.points != null ? ` ${sl.market.points}` : '';
            const key = `${sl.market.marketType}|${sl.market.period ?? 1}|${sl.market.points ?? ''}`;
            return (
              <tr key={key} className="border-border/40 border-b">
                <td className="text-foreground/80 w-60 px-2 py-1 text-xs">
                  {label}
                  {line}
                  {period}
                </td>
                <td className="px-2 py-1 text-center">
                  <span className="text-muted-foreground mr-1 text-[10px] uppercase">{fam.away}</span>
                  <OpOddsText slice={sl} side="away" rowHomeCode={sl.homeTeam} />
                </td>
                <td className="px-2 py-1 text-center">
                  <span className="text-muted-foreground mr-1 text-[10px] uppercase">{fam.home}</span>
                  <OpOddsText slice={sl} side="home" rowHomeCode={sl.homeTeam} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface BetRow {
  synth: GameRow;
  /** undefined → away/home pair (binary); set → single-outcome row. */
  singleSide?: TeamSide;
  /** This row continues the previous group (the 1H Tie under the away/home
   *  pair) — rendered with the pair's gameIndex so all three outcomes share
   *  one zebra background, and without the game-boundary top border. */
  continuation?: boolean;
  /** Canonical game key for the Odds (consensus) lookup. Set only for the soccer
   *  "90 min" moneyline (the demoted full-game 1X2) — it owns the same Unabated
   *  consensus the round-robin moneyline pairs. Other periods (1H/2H) have no
   *  full-game consensus, so they leave it undefined → "—". */
  consensusKey?: string;
  /** This row is the Draw outcome → read the consensus' drawFairProbCents. */
  oddsOutcome?: 'draw';
  /** Full-feed B: pin the row's OP/PNCL cell to a period/line bucket. */
  opBucket?: OpBucketRef;
  /** Zebra/border group for Props/Events ladder rows (one group per player /
   *  per team-total ladder / per binary). Rungs after the first in a group set
   *  `continuation`; visibility-resolved continuation is recomputed at render
   *  so a hidden first rung doesn't orphan its successor. Unset on Game Lines
   *  tab rows (those keep the moneyline pair/Tie logic). */
  groupIndex?: number;
  /** Present on value-ladder rungs (a player+stat prop, a team-total). Marks
   *  this row as one rung of a group the Compact bands collapse into a single
   *  row with a rung selector (#141). `groupKey` is the ladder's stable
   *  identity WITHOUT the threshold, so different players / stats / teams never
   *  share one; `rungLabel` is the chip text ("2+"); `groupLabel` is the band
   *  title without the number ("F. Tatis (SD) Hits"). The Detailed table
   *  ignores this and keeps one row per rung. */
  ladder?: { groupKey: string; groupLabel: string; rungLabel: string };
}

/** A row is "settled" when every venue book with a known market status is
 *  terminal (TERMINAL_STATUSES — closed/settled/finalized/determined) and at
 *  least one status is known. Quarter bets resolve mid-game, so a live game's
 *  table mixes settled and working lines: settled rows stay visible with a
 *  SETTLED chip by default (label, don't hide — Brendan Jun 11) and the
 *  "Hide settled" toggle drops them for a working-lines-only view. Reads
 *  marketStore imperatively; the component's `version` subscription keeps
 *  the result fresh across status flips. */
function rowIsSettled(synth: GameRow): boolean {
  const store = useMarketStore.getState();
  const statuses: string[] = [];
  const add = (b: VenueBook | null | undefined) => {
    const status = b?.marketTicker ? store.getMarket(b.marketTicker)?.status : null;
    if (status) statuses.push(status);
  };
  add(synth.kalshi?.away);
  add(synth.kalshi?.home);
  add(synth.polymarket?.away);
  add(synth.polymarket?.home);
  return statuses.length > 0 && statuses.every((st) => TERMINAL_STATUSES.has(st));
}

/** A row has "real" volume when any of its venue books has traded contracts.
 *  Zero across the board = an empty placeholder market (e.g. Polymarket's
 *  2/98, 0-volume tail strikes) — hidden by the "Hide 0-Vol" toggle. */
function rowHasVolume(synth: GameRow): boolean {
  const traded = (b: VenueBook | null | undefined) => (b?.volume ?? 0) > 0;
  return (
    traded(synth.kalshi?.away) ||
    traded(synth.kalshi?.home) ||
    traded(synth.polymarket?.away) ||
    traded(synth.polymarket?.home)
  );
}

export function GameLinesSection({ game, focusTicker, readingFocus, focusUsMarket, focusSide, onPillClick, onUsPillClick, onActiveLineChange, panelLine = null, onPanelLineChange }: Props) {
  const version = useMarketStore((s) => s.version);
  // Inherit the user's defaults exactly like MoneylineSection / SpreadTotalSection:
  // price mode from displaySettings; side = 'no' (the app-wide convention — there's
  // no persisted YES/NO preference). Hydra now streams the 1H books live over WS, so
  // Maker no longer lags here and there's no reason to override the price mode.
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const gameLinesViewMode = useDisplaySettings((s) => s.gameLinesViewMode);
  const setGameLinesViewMode = useDisplaySettings((s) => s.setGameLinesViewMode);
  // OP (OddsPapi) column lockstep with the shared GameRows: shown only when
  // this game has OP coverage AND the user toggle is on (auto-hide — the
  // section is pixel-identical to pre-feature otherwise). OP is full-game
  // moneyline only, so period/prop rows render "—" in the column; the
  // demoted "90 min" moneyline row carries the value via its consensusKey.
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  // A tennis key is shared by both tours — show only this tour's frame.
  const gameOpSlice = opSliceForSport(useOpOddsFor(showOpOdds ? game.key : null), game.sport);
  const hideOpColumn = gameOpSlice == null;
  // Odds filter (Dan 2026-08-23): U defaults OFF; thead + GameRows lockstep.
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const hideOddsColumn = !showUnbtdOdds;
  // D-020 LT2-005 — Poly-only line hiding, same rule as SpreadTotalSection.
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const setShowPolyOnlyLines = useDisplaySettings((s) => s.setSpreadTotalShowPolyOnlyLines);
  const butterflySetting = useDisplaySettings((s) => s.spreadTotalNearestLines);
  const butterflyOn = lt2Access && butterflySetting && gameLinesViewMode === 'compact';
  const hidePolyOnlyLines = lt2Access && (!showPolyOnlyLines || butterflyOn);
  const openOrders = useAccountStore((s) => s.openOrders);
  const resumeLatest = useGameDetailResumesLatest();
  const reading = useWorkingView(gameLinesReading, readingInstance(game.key, readingFocus === undefined ? focusTicker : readingFocus, focusSide), {
    priceMode: defaultPriceMode, marketSide: 'no', betType: 'moneyline',
    hideNoVol: true, hideSettled: false, period: game.sport === 'soccer' ? 'full' : '1H',
    tab: 'lines', propStat: 'hits',
  }, { resumeLatest });
  const [priceMode, setPriceMode] = useWorkingViewField(reading, 'priceMode');
  const [marketSide, setMarketSide] = useWorkingViewField(reading, 'marketSide');
  const [betType, setBetType] = useWorkingViewField(reading, 'betType');
  const [expanded, setExpanded] = useState<{ key: string; side: TeamSide } | null>(null);
  // "Hide 0-Vol": drop rows whose markets have never traded (volume 0). On by
  // default to cut the empty placeholder lines (e.g. Poly's 2/98 tail strikes);
  // toggle off to see every open market, traded or not.
  const [hideNoVol, setHideNoVol] = useWorkingViewField(reading, 'hideNoVol');
  // "Hide settled": resolved rows are LABELED by default (SETTLED chip,
  // dimmed) rather than removed — traders wanted settlement prices visible
  // next to working lines. Toggle on to drop them entirely.
  const [hideSettled, setHideSettled] = useWorkingViewField(reading, 'hideSettled');
  // Compact-view (EventRows) line selection + expand — separate from the
  // detailed view's `expanded` {key,side}. Only used on the spread/total tabs.
  const [compactSpreadIdx, setCompactSpreadIdx] = useState<number | null>(null);
  const [compactTotalIdx, setCompactTotalIdx] = useState<number | null>(null);
  const [compactSpreadKey, setCompactSpreadKey] = useState<string | null>(null);
  const [compactTotalKey, setCompactTotalKey] = useState<string | null>(null);
  const [compactExpandedRow, setCompactExpandedRow] = useState<'spread' | 'total' | null>(null);
  /** Expanded compact BAND, by the outcome's own key (#105). */
  const [expandedBandKey, setExpandedBandKey] = useState<string | null>(null);
  /** Selected rung per collapsed ladder band (#141): groupKey → the picked
   *  rung's stable chip id (its market ticker). Stored by IDENTITY, never array
   *  position, so hiding / settling a lower rung can't shift which threshold
   *  shows ([[state-by-identity-not-grouping-key]]). Absent — or a stored rung
   *  no longer listed — falls back to the closest-to-50¢ default. Reset on
   *  structural change below. */
  const [rungKeyByGroup, setRungKeyByGroup] = useState<Map<string, string>>(new Map());
  /** Which ladder band currently owns the open trade ticket, and on which
   *  venue — so switching that band's rung can move the ticket with it (#141).
   *  Without this the row could show 5+ while the ticket still holds 6+, the
   *  wrong-line divergence the Game Total strip never has (it re-points the
   *  panel on every arrow). The order side comes from the live marketSide. */
  const [openBandTicket, setOpenBandTicket] = useState<{
    key: string;
    venue: 'kalshi' | 'polymarket';
  } | null>(null);

  // Soccer's primary frame is the full 90-minute game (the demoted 1X2 on
  // knockouts, the main BTTS line otherwise) — default there; every other sport
  // starts on the 1st half. effectivePeriod falls back to availForBet[0] when the
  // default isn't listed (e.g. group-stage soccer has no 'full' moneyline → 1H).
  const [selectedPeriod, setSelectedPeriod] = useWorkingViewField(reading, 'period');
  /** Which collapsed LADDER owns the right-side panel, by its groupKey. The
   *  panel then renders the ladder's own threshold strip, so a rung can be
   *  switched from the order entry itself — the parity with Spread/Total Dan
   *  asked for (2026-07-24). Null = a plain row / another section owns it. */
  const [panelLadderKey, setPanelLadderKey] = useState<string | null>(null);
  // A new URL focus is a new native-contract selection, even when the same
  // reading view and component remain mounted. On a sport whose props list
  // two native contracts at one threshold (NHL SAVE / SAVES, NBA STOCK /
  // STOCKS — lib/sport/propStat), a retained preference must not hide the new
  // target before its focus effect runs.
  const twinSeriesProps = propSportHasTwinSeries(game.sport);
  const [rungFocus, setRungFocus] = useState({ gameKey: game.key, ticker: focusTicker });
  if (rungFocus.gameKey !== game.key || rungFocus.ticker !== focusTicker) {
    setRungFocus({ gameKey: game.key, ticker: focusTicker });
    if (twinSeriesProps) {
      setRungKeyByGroup(new Map());
      setPanelLadderKey(null);
      setOpenBandTicket(null);
    }
  }

  // ── Player Props / Game Props tabs (mlb-props-events, cfb-game-props,
  // nfl-player-props + nfl-game-lines-props) ─────────────────────────
  // Kalshi-only surfaces on the same live feed; the selectors return empty
  // until the feed side ships (and props until lineups post) — the tabs then
  // render disabled, which is the designed degrade, not an error.
  const isMlb = game.sport === 'mlb';
  const [tab, setTab] = useWorkingViewField(reading, 'tab');
  // Keyed off the RAW selection, not effectiveTab: with the worker's
  // prop-frame discard on, a props tab has no data yet when clicked
  // (effectiveTab would fall back to 'lines') — the click itself is what
  // lifts the discard; the resnapshot then backfills and the tab fills.
  useEffect(() => {
    if (tab === 'props' || tab === 'events') requestPropsFeed();
  }, [tab]);
  const isNfl = game.sport === 'nfl';
  // NHL (#1218): period lines, team totals + overtime (Game Props) and player
  // props (Player Props), all Kalshi-only from NHLALT, manual ticket only.
  const isNhl = game.sport === 'nhl';
  const isUsPlayerSport = isNfl || game.sport === 'nba';
  const usCatalogue = useUsPlayerCatalogue(game.key, tab === 'props' || tab === 'events', true);
  const usPlayers = useMemo(() => ({ ...usCatalogue, markets: usCatalogue.markets.filter(m => usPlayerTarget(m)) }), [usCatalogue]);
  const usGameProps = useMemo(() => ({ ...usCatalogue, markets: usCatalogue.markets.filter(m => usGamePropTarget(m)) }), [usCatalogue]);
  const usFocusHandled = useRef<string | null>(null);
  useEffect(() => {
    const request = focusUsMarket ? `${game.key}|${focusUsMarket}|${focusSide ?? 'no'}` : null;
    if (!request) { usFocusHandled.current = null; return; }
    if (usFocusHandled.current === request || usCatalogue.status !== 'ready') return;
    const matches = usCatalogue.markets.filter(m => m.marketSlug === focusUsMarket);
    if (matches.length !== 1) return;
    const target = usPlayerTarget(matches[0]), gameTarget = usGamePropTarget(matches[0]);
    if (!target && !gameTarget) return;
    usFocusHandled.current = request;
    // Existing deep-link effects seed section state and the shared ticket.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTab(target ? 'props' : 'events');
    onPanelLineChange?.(null); onActiveLineChange?.(null);
    onPillClick({ game: target ? usPlayerPanelGame(game, target, matches[0].title) : usGamePropPanelGame(game, gameTarget!, matches[0].title), teamSide: 'away',
      venue: 'polymarket', initialOrderSide: focusSide ?? 'no', postOnly: false });
  }, [game, focusUsMarket, focusSide, usCatalogue, setTab, onPanelLineChange, onActiveLineChange, onPillClick]);
  // The default is data-driven below (activeStatGroup falls back to the first
  // listed group), so the initial id only matters once a chip is clicked.
  const [propStat, setPropStat] = useWorkingViewField(reading, 'propStat');
  // Player Props: every sport with a prop vocabulary (lib/sport/propStat) —
  // MLB via ticker classification + the Poly join; the wire prop sports (NFL,
  // NHL, NBA) by the wire class, Kalshi-only. No per-sport branch: a sport
  // joins by its table entry.
  const propSport = isPropStatSport(game.sport) ? game.sport : null;
  const playerProps = useMemo<PlayerPropStatGroup[]>(() => {
    void version;
    // Twin series (NHL SAVE / SAVES) can name two native contracts at the same
    // threshold. Keep the existing focused/selected ticker even when its
    // sibling updates.
    const preferred = [...rungKeyByGroup.values()];
    if (focusTicker) preferred.push(focusTicker.toUpperCase());
    return propSport ? useMarketStore.getState().getPlayerProps(propSport, game.key, preferred) : [];
  }, [game.key, propSport, version, rungKeyByGroup, focusTicker]);
  const eventMarkets = useMemo<MlbEventMarkets | null>(() => {
    void version;
    if (!isMlb) return null;
    return useMarketStore.getState().getMlbEventMarkets(game.key);
  }, [game.key, isMlb, version]);
  // ── CFB Events tab (cfb-game-props, 2026-08-29) ────────────────────
  // The Kalshi game-event families classified by the WIRE marketType
  // (lib/cfbWireMarkets): 1H/FT result, first to score / first TD team,
  // highest-scoring quarter (enum panes), overtime / 2-point / D/ST TD
  // (yes-no rows), per-team stat O/U ladders, total TD / FG ladders. No
  // Player Props tab for CFB — Kalshi lists none beyond D/ST TD, which rides
  // Events. Renders only once core ships the classes (rule 8 until then).
  const isCfb = game.sport === 'cfb';
  // NFL (nfl-game-lines-props, 2026-09-03) rides the same wire-classified
  // selector: 1H/FT result, first-TD team, 2-point, every-quarter BTTS, total
  // touchdowns, team totals (full game + period), plus the NFL-only race-to
  // and both-teams-points once core ships those classes. Kalshi files
  // Overtime and Race to Points under GAME LINES for the NFL, so those two
  // render there (bet-type chips) rather than on Game Props.
  const isFootball = isCfb || isNfl;
  // NHL's team totals and overtime ride the same wire classifier (#1218) and
  // render on Game Props — Kalshi files them beside the props, not the lines.
  const hasWireEvents = isFootball || isNhl;
  const cfbEvents = useMemo<CfbEventMarkets | null>(() => {
    void version;
    if (!hasWireEvents) return null;
    return useMarketStore.getState().getCfbEventMarkets(game.key);
  }, [game.key, hasWireEvents, version]);
  const nflOvertime = isNfl ? cfbEvents?.binaries.find((b) => b.cls === 'overtime') ?? null : null;
  // Memoised so the `[]` fallback keeps one identity across renders (it feeds
  // the orderbook-subscription memo's deps).
  const nflRaces = useMemo(() => (isNfl ? cfbEvents?.races ?? [] : []), [isNfl, cfbEvents]);
  /** Whether the Game Props tab has anything to show — for the NFL, the
   *  overtime binary lives on Game Lines and must not enable an empty tab. */
  const footballEventsHaveRows = !!cfbEvents && (
    cfbEvents.enums.length > 0
    || cfbEvents.binaries.some((b) => !(isNfl && b.cls === 'overtime'))
    || cfbEvents.teamStats.length > 0
    || cfbEvents.totals.length > 0
    || cfbEvents.bothTeamsPoints.length > 0
  );
  // ── UFC Fight Props (#848) ─────────────────────────────────────────
  // Kalshi's per-bout series beside the fight winner (total rounds, method
  // of victory / finish, round of victory, goes the distance), admitted to
  // this fight by the producer's canonical key AND the bout's exact Kalshi
  // event code (lib/ufc/fightPropSelection). The fight winner stays the
  // board row and the Moneyline section above; these render only here.
  const isUfc = game.sport === 'ufc';
  const ufcProps = useMemo<UfcFightProps | null>(() => {
    void version;
    if (!isUfc) return null;
    return useMarketStore.getState().getUfcFightProps(game.key);
  }, [game.key, isUfc, version]);
  // Polymarket fight props (carried from node10): the per-fight Yes/No and
  // O/U-rounds markets on the fight's own Poly moneyline slug stem, so a
  // Poly-only fight gets them too. Rendered on the same Fight Props tab below
  // the Kalshi families; a boxing bout shows the tab only when it has some.
  const isFightSport = isUfc || game.sport === 'boxing';
  const fightPolyTicker = isFightSport ? game.polymarket?.away.marketTicker ?? null : null;
  const polyFightProps = useMemo<PolyFightPropRow[]>(() => {
    void version;
    if (!fightPolyTicker) return [];
    return useMarketStore.getState().getPolyFightProps(fightPolyTicker);
  }, [fightPolyTicker, version]);
  const isFightPropsTab = isUfc || (isFightSport && polyFightProps.length > 0);
  /** Sports whose section carries the Game Lines | (Player Props) | Events
   *  selector; everything else is pinned to 'lines'. UFC shows its single
   *  Fight Props tab as the section title. */
  const hasTabs = isMlb || isCfb || isUsPlayerSport || isNhl || isFightPropsTab;
  const hasProps = playerProps.length > 0 || isUsPlayerSport && usPlayers.markets.length > 0;
  // Consumer feed filters: this section renders everything the front page
  // does not (player props, game-event families, team totals, btts, RFI,
  // extras), so while it is mounted the feed must carry the classes the
  // browser defaults exclude. Declared per game key so two open details
  // (dual-tab desks) each hold their own opt-in.
  // Sports axis: this section renders ONE game, so it declares that game's
  // sport and nothing else. Omitting the field would mean "every sport" (see
  // FeedNeeds) — a mounted child would then re-broaden the fleet and cancel
  // the route-sport narrowing its own page just declared, which is exactly
  // what a game detail looked like on g1.pm (Dan, 2026-09-03). A row with no
  // sport declares none: the page's own route declaration carries the breadth.
  // Games axis: the SAME event the page declares. Omitting it would mean every
  // game (see FeedNeeds), and this section mounts inside the page — so an
  // omission here would cancel the page's own event narrowing, exactly as the
  // missing sports field did on 2026-09-03.
  const gameWireKeys = useMemo(
    () => useMarketStore.getState().getGameWireKeys(game.key),
    [game.key],
  );
  // Both keys of a split sport (main-alt-partition-he6-he7 D5.3): the period
  // lines and game props this section renders arrive under `<SPORT>ALT` from
  // the ALT edge once the sport is split, and only a source naming that key
  // unmutes the edge. Same shape as the page's own route declaration — and
  // the same rule (g1xl-v2#1053 Part 2c): the ALT key is named only beside
  // the games axis, never for the whole sport.
  // Rows: none of its own (g1xl-v2#1053, 2026-10-13). The page names this
  // game's rows by ticker (`useRouteSportFeedNeeds`); a ticker-BROAD source
  // here would keep that list off the wire and hand the main edge the whole
  // sport beside any board. The fold above still carries the game wherever
  // the list cannot ride.
  useFeedNeeds(`game-lines:${game.key}`, {
    classes: GAME_DETAIL_CLASSES,
    sports: game.sport ? (gameWireKeys.length > 0 ? [game.sport, altTokenFor(game.sport)] : [game.sport]) : [],
    games: gameWireKeys,
    tickers: [],
  });
  const hasEvents = eventMarkets != null || footballEventsHaveRows || usGameProps.markets.length > 0 || ufcProps != null
    || polyFightProps.length > 0;

  // Sports with period/BTTS markets in this table: NBA (1H + quarters), WNBA
  // (1H only), soccer (World Cup 1H/2H spread/total/moneyline + BTTS at
  // full/1H/2H), MLB (First 5 Innings moneyline/spread/total, Kalshi-only), and
  // NFL (1H/2H moneyline/spread/total, Kalshi-only — #543).
  // Other sports render nothing here (their full-game lines live in the top
  // Moneyline / Spread-Total sections).
  const supportsPeriods =
    game.sport === 'nba' || game.sport === 'wnba' || game.sport === 'soccer' || game.sport === 'mlb' ||
    // Gates opened for the he2 football slate (Dan, 2026-08-18): period
    // tabs stay data-driven, so these render only when depth actually
    // arrives from the edge.
    game.sport === 'cfb' || game.sport === 'nfl' ||
    // NHL 1st/2nd/3rd period winner / spread / total / BTTS (#1218).
    isNhl;

  // Which periods have data, by bet type (one cheap scan). Drives the period
  // tabs + bet-type availability. Data-driven: WNBA shows only 1H, soccer shows
  // 1H/2H (+ Full Game under BTTS), NBA shows 1H + quarters.
  const avail = useMemo(() => {
    void version;
    const empty = {
      spread: [] as Period[], total: [] as Period[], moneyline: [] as Period[], btts: [] as Period[],
    };
    if (!supportsPeriods) return empty;
    return useMarketStore.getState().getAvailablePeriods(game.key);
  }, [game.key, supportsPeriods, version]);

  const hasSpread = avail.spread.length > 0;
  const hasTotal = avail.total.length > 0;
  const hasMoneyline = avail.moneyline.length > 0;
  const hasBtts = avail.btts.length > 0;
  const hasRace = nflRaces.length > 0;
  const hasOvertime = nflOvertime != null;
  const hasLines = hasSpread || hasTotal || hasMoneyline || hasBtts || hasRace || hasOvertime;
  // Selected tab, corrected for availability: a tab whose data drained (or
  // never arrived) falls back to the leftmost tab that has markets. Sports
  // without the selector (hasTabs) are pinned to 'lines'.
  const effectiveTab: SectionTab = !hasTabs
    ? 'lines'
    : (tab === 'props' && hasProps) || (tab === 'events' && hasEvents) || (tab === 'lines' && hasLines)
      ? tab
      : hasLines ? 'lines' : hasProps ? 'props' : hasEvents ? 'events' : 'lines';
  // Stat chips are data-driven like the period chips — only stats with ingested
  // markets render; the selection falls back to the first available stat.
  const activeStatGroup = playerProps.find((g) => g.stat === propStat) ?? playerProps[0] ?? null;
  // Order mirrors BET_TYPES so the auto-selected default (availableBets[0]) is
  // the leftmost available tab.
  const availableBets: BetType[] = [
    ...(hasMoneyline ? (['moneyline'] as const) : []),
    ...(hasTotal ? (['total'] as const) : []),
    ...(hasSpread ? (['spread'] as const) : []),
    ...(hasBtts ? (['btts'] as const) : []),
    ...(hasRace ? (['race'] as const) : []),
    ...(hasOvertime ? (['overtime'] as const) : []),
  ];
  const effectiveBetType: BetType = availableBets.includes(betType) ? betType : availableBets[0] ?? 'moneyline';

  // Periods valid for the active bet type (data-driven — CFB lists quarter
  // winners, KXNCAAF1Q–4Q; the NBA does not). BTTS additionally offers Full
  // Game (no top section).
  const availForBet: Period[] =
    effectiveBetType === 'spread' ? avail.spread
      : effectiveBetType === 'total' ? avail.total
        : effectiveBetType === 'moneyline' ? avail.moneyline
          : effectiveBetType === 'btts' ? avail.btts
            // Race-to / overtime are full-game families — no period chips.
            : [];
  const effectivePeriod: Period = availForBet.includes(selectedPeriod) ? selectedPeriod : availForBet[0] ?? '1H';

  // Period chips rendered in availForBet order (getAvailablePeriods returns
  // 'full' first), so soccer's "90 min" leads. Non-soccer bet types never carry
  // 'full' here, so this order is identical to the period-natural PERIODS order
  // for them — only soccer's full game moves to the front.
  const periodChips = availForBet
    .map((id) => PERIODS.find((p) => p.id === id))
    .filter((p): p is (typeof PERIODS)[number] => !!p);

  // Spread/total lines for the active period (Kalshi-anchored; quarters + soccer
  // periods have an empty Poly column in Phase 1 by design). Only built for the
  // spread/total bet types — moneyline/BTTS source their own books below.
  const event = useMemo<GameEventRow | null>(() => {
    void version;
    if (!supportsPeriods || effectiveTab !== 'lines') return null;
    if (effectiveBetType !== 'spread' && effectiveBetType !== 'total') return null;
    const found =
      useMarketStore
        .getState()
        .getGameEvents(game.sport, { dateFilter: 'all', statusFilter: 'all' }, effectivePeriod)
        .find((e) => e.key === game.key) ?? null;
    // D-020 LT2-005: hide Poly-only lines from the execution view under lt2
    // (reveal toggle un-hides; revealed lines stay disabled). lt2 off →
    // untouched.
    if (found && hidePolyOnlyLines) return filterEventExecutableLines(found, false);
    return found;
  }, [game.key, game.sport, supportsPeriods, effectiveTab, effectiveBetType, effectivePeriod, version, hidePolyOnlyLines]);

  // 3-way period winner (NBA/WNBA 1H, soccer 1H/2H, MLB F5) — only for the
  // periods that actually list a 3-way winner. Soccer also accepts 'full': the
  // regulation 1X2 demoted under "90 min" once the knockout "To Advance" market
  // takes the top Moneyline slot (getAvailablePeriods gates 'full' to that case).
  const moneyline = useMemo(() => {
    void version;
    if (!supportsPeriods || effectiveTab !== 'lines' || effectiveBetType !== 'moneyline') return null;
    // Any non-full period: halves / F5 everywhere, plus CFB's quarter winners
    // (KXNCAAF1Q–4Q, 3-way like the halves). getAvailablePeriods only offers a
    // period that actually has winner rows, so this never renders an empty tab.
    if (effectivePeriod !== 'full') {
      return useMarketStore.getState().getPeriodMoneyline(game.key, effectivePeriod);
    }
    if (effectivePeriod === 'full' && game.sport === 'soccer') {
      return useMarketStore.getState().getPeriodMoneyline(game.key, 'full');
    }
    return null;
  }, [game.key, game.sport, supportsPeriods, effectiveTab, effectiveBetType, effectivePeriod, version]);

  // Both-teams-to-score binary for the active period (soccer; full/1H/2H).
  const bttsBooks = useMemo(() => {
    void version;
    if (!supportsPeriods || effectiveTab !== 'lines' || effectiveBetType !== 'btts') return null;
    return useMarketStore.getState().getBtts(game.key, effectivePeriod);
  }, [game.key, supportsPeriods, effectiveTab, effectiveBetType, effectivePeriod, version]);

  // Subscribe depth for just the tickers shown (the active period's lines + the
  // winner when moneyline is up + the BTTS book). PosCell + ladders read these.
  const orderbookTickers = useMemo<string[]>(() => {
    const set = new Set<string>();
    const add = (b: VenueBook | null | undefined) => { if (b?.marketTicker) set.add(b.marketTicker); };
    for (const s of event?.spreads ?? []) { add(s.kalshi); add(s.polymarket); }
    for (const t of event?.totals ?? []) { add(t.kalshi); add(t.polymarket); }
    if (moneyline) {
      add(moneyline.away.kalshi); add(moneyline.away.polymarket);
      add(moneyline.home.kalshi); add(moneyline.home.polymarket);
      add(moneyline.tie.kalshi); add(moneyline.tie.polymarket);
    }
    if (bttsBooks) { add(bttsBooks.kalshi); add(bttsBooks.polymarket); }
    // MLB Props/Events tabs: subscribe only what's rendered — the selected
    // stat's ladders (prop cardinality is large; the other stats' books load
    // on chip switch), and every Events market (small set). Poly PROP tickers
    // are deliberately NOT added: props have no orderbooks on either engine
    // (static Gamma snapshot, never WS-subscribed — hydra's orderbook
    // endpoint 404s them). The Poly RFI/extras binaries DO have live WS
    // books, so they subscribe like any game line.
    if (effectiveTab === 'props' && activeStatGroup) {
      for (const pl of activeStatGroup.players) for (const rung of pl.rungs) add(rung.kalshi);
    }
    if (effectiveTab === 'events' && eventMarkets) {
      add(eventMarkets.rfi?.kalshi);
      add(eventMarkets.rfi?.polymarket);
      add(eventMarkets.extras?.kalshi);
      add(eventMarkets.extras?.polymarket);
      for (const tt of eventMarkets.teamTotals) for (const rung of tt.rungs) add(rung.kalshi);
    }
    if (effectiveTab === 'events' && cfbEvents) {
      for (const g of cfbEvents.enums) for (const o of g.outcomes) add(o.kalshi);
      for (const b of cfbEvents.binaries) add(b.kalshi);
      for (const l of cfbEvents.teamStats) for (const ln of l.lines) add(ln.kalshi);
      for (const t of cfbEvents.totals) for (const ln of t.lines) add(ln.kalshi);
      for (const ln of cfbEvents.bothTeamsPoints) add(ln.kalshi);
    }
    if (effectiveTab === 'events' && ufcProps) {
      for (const g of ufcProps.groups) for (const r of g.rows) add(r.kalshi);
    }
    if (effectiveTab === 'events') for (const fp of polyFightProps) add(fp.polymarket);
    if (effectiveTab === 'lines' && effectiveBetType === 'overtime') add(nflOvertime?.kalshi);
    if (effectiveTab === 'lines' && effectiveBetType === 'race') {
      for (const line of nflRaces) for (const o of line.outcomes) add(o.kalshi);
    }
    return Array.from(set);
  }, [event, moneyline, bttsBooks, effectiveTab, effectiveBetType, activeStatGroup, eventMarkets, cfbEvents, ufcProps, polyFightProps, nflOvertime, nflRaces]);
  const tickersKey = orderbookTickers.slice().sort().join('|');

  // Per-user Kalshi positions + resting orders — drives PosCell (position ring
  // + open-order chips) and the ladder's resting-order chips. Kalshi-only.
  const kalshiTickers = useMemo(
    () => orderbookTickers.filter((t) => !t.startsWith('PM-')),
    [orderbookTickers],
  );
  // #666: these sections render HUMAN order chips only, so they read the
  // display lane (bounded per-order retention with provenance) exactly like
  // the Value Dashboard's humanOrdersByTicker. Strict fail-closed consumers
  // (alerts/automation) live elsewhere and keep the strict lane.
  const { positionByTicker, ordersByTicker: strictOrdersByTicker, displayOrdersByTicker } =
    useUserKalshiPOS(kalshiTickers);
  const ordersByTicker = displayOrdersByTicker ?? strictOrdersByTicker;
  useEffect(() => {
    const viewId = `game-lines:${game.key}`;
    useMarketStore.getState().setOrderbookNeeds(viewId, orderbookTickers);
    return () => useMarketStore.getState().setOrderbookNeeds(viewId, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.key, tickersKey]);

  // Compact-view (EventRows) line defaults: closest-to-50¢ per strip, sticky
  // once the user scrolls. Only meaningful when `event` exists (spread/total).
  const compactAvailable = event != null;
  const compactDefaultSpreadIdx = useMemo(
    () => (event ? closestKalshiYesTo50(event.spreads.map(linePriceCents)) : 0),
    [event],
  );
  const compactDefaultTotalIdx = useMemo(
    () => (event ? closestKalshiYesTo50(event.totals.map(linePriceCents)) : 0),
    [event],
  );
  // Reset the sticky chip selection when the underlying line set changes
  // (different game, period, or bet-type). This section's line set changes
  // under the user — and EventRows indexes the array raw — so a scrolled index
  // from a larger strip would otherwise point past a smaller one and render a
  // blank, dead row. (SpreadTotalSection is immune: its event is the one stable
  // full-game event.)
  useEffect(() => {
    setCompactSpreadIdx(null);
    setCompactTotalIdx(null);
    setCompactSpreadKey(null);
    setCompactTotalKey(null);
    setCompactExpandedRow(null);
  }, [game.key, effectivePeriod, effectiveBetType]);
  // Same idea for the bands, but keyed on the outcome set — which also
  // changes with the section tab and the selected prop stat. A stale key is
  // harmless (nothing matches, nothing opens), but leaving it set means a
  // band silently re-opens if you navigate back to the same outcome.
  useEffect(() => {
    setExpandedBandKey(null);
    // Drop sticky rung selections too — the outcome set (and so the ladders)
    // changed. Keyed by groupKey, stale entries are harmless, but clearing
    // keeps a re-visited ladder from silently re-opening on an old rung.
    setRungKeyByGroup((prev) => (prev.size === 0 ? prev : new Map()));
    // The open ticket belonged to a band/ladder that no longer exists here.
    setOpenBandTicket(null);
    setPanelLadderKey(null);
  }, [game.key, effectiveTab, effectiveBetType, effectivePeriod, activeStatGroup?.stat]);
  // #608 — see the twin in SpreadsTotals/index.tsx. Here the card id also
  // carries the PERIOD, so the clear must name the period currently on
  // screen; periods outside `lt2PeriodFor` register no card at all and are
  // skipped rather than cleared under a guessed id.
  useLayoutEffect(() => {
    if (!event || lt2PeriodFor(effectivePeriod) == null) return;
    const prefix = `gl:${effectivePeriod}`;
    const { clearFocusIf } = useLt2FocusStore.getState();
    if (
      compactSpreadKey != null &&
      !event.spreads.some((l) => spreadLineKey(l) === compactSpreadKey)
    ) {
      clearFocusIf(lt2CardId(prefix, event.key, 'spread'), { marketGone: true });
    }
    if (
      compactTotalKey != null &&
      !event.totals.some((l) => totalLineKey(l) === compactTotalKey)
    ) {
      clearFocusIf(lt2CardId(prefix, event.key, 'total'), { marketGone: true });
    }
  }, [event, effectivePeriod, compactSpreadKey, compactTotalKey]);

  // Where the held key sits in the lines on screen (-1: none held, or gone).
  const compactSpreadKeyIdx = event && compactSpreadKey != null
    ? event.spreads.findIndex((l) => spreadLineKey(l) === compactSpreadKey)
    : -1;
  const compactTotalKeyIdx = event && compactTotalKey != null
    ? event.totals.findIndex((l) => totalLineKey(l) === compactTotalKey)
    : -1;
  // LT2 resolves the compact selection by market identity so live line-array
  // reorder cannot retarget it. Flag-off preserves the legacy index clamp for
  // chip picks; a line held by touching it (H-4, below) holds by identity.
  const activeCompactSpreadIdx = lt2Access && event
    ? resolveLineIndexByKey(
        event.spreads,
        compactSpreadKey,
        spreadLineKey,
        compactDefaultSpreadIdx,
      )
    : Math.min(
        compactSpreadIdx ?? (compactSpreadKeyIdx >= 0 ? compactSpreadKeyIdx : compactDefaultSpreadIdx),
        Math.max(0, (event?.spreads.length ?? 0) - 1),
      );
  const activeCompactTotalIdx = lt2Access && event
    ? resolveLineIndexByKey(
        event.totals,
        compactTotalKey,
        totalLineKey,
        compactDefaultTotalIdx,
      )
    : Math.min(
        compactTotalIdx ?? (compactTotalKeyIdx >= 0 ? compactTotalKeyIdx : compactDefaultTotalIdx),
        Math.max(0, (event?.totals.length ?? 0) - 1),
      );
  // LT2-006 — aiming a card pins the line on screen (see usePinLineOnAim);
  // the card id carries the period, and periods outside lt2PeriodFor
  // register no card, so they pin nothing.
  const pinPrefix =
    lt2Access && event && lt2PeriodFor(effectivePeriod) != null ? `gl:${effectivePeriod}` : null;
  const displayedCompactSpread = event?.spreads[activeCompactSpreadIdx];
  const displayedCompactTotal = event?.totals[activeCompactTotalIdx];
  usePinLineOnAim({
    cardId: pinPrefix && event ? lt2CardId(pinPrefix, event.key, 'spread') : null,
    // A held key that is no longer listed pins nothing, so aiming pins the
    // line on screen (as Spread / Total does) instead of the #608 clear
    // dropping the aim on the next frame.
    pinnedKey: compactSpreadKeyIdx >= 0 ? compactSpreadKey : null,
    displayedKey: displayedCompactSpread ? spreadLineKey(displayedCompactSpread) : null,
    pin: setCompactSpreadKey,
  });
  usePinLineOnAim({
    cardId: pinPrefix && event ? lt2CardId(pinPrefix, event.key, 'total') : null,
    pinnedKey: compactTotalKeyIdx >= 0 ? compactTotalKey : null,
    displayedKey: displayedCompactTotal ? totalLineKey(displayedCompactTotal) : null,
    pin: setCompactTotalKey,
  });
  // H-4 — the same hold as Spread / Total: a held line stays on screen while
  // prices move; one that leaves the board is said so (once the line list has
  // held still) rather than silently replaced. Flag-off chip picks hold by
  // index, as they always have.
  const compactSpreadMissing = compactSpreadKey != null && (event?.spreads.length ?? 0) > 0 && compactSpreadKeyIdx < 0;
  const compactTotalMissing = compactTotalKey != null && (event?.totals.length ?? 0) > 0 && compactTotalKeyIdx < 0;
  const compactSpreadMissingSettled = useLineListSettled(
    compactSpreadMissing, `${compactSpreadKey}|${event?.spreads.map(spreadLineKey).join(',') ?? ''}`,
  );
  const compactTotalMissingSettled = useLineListSettled(
    compactTotalMissing, `${compactTotalKey}|${event?.totals.map(totalLineKey).join(',') ?? ''}`,
  );
  const compactSpreadHeld = !compactSpreadMissing
    && (compactSpreadKeyIdx >= 0 || (!lt2Access && compactSpreadIdx != null));
  const compactTotalHeld = !compactTotalMissing
    && (compactTotalKeyIdx >= 0 || (!lt2Access && compactTotalIdx != null));
  const heldSpreadLabel = compactSpreadHeld && displayedCompactSpread && event
    ? spreadLineLabel(displayedCompactSpread, event.awayCode) : null;
  const heldTotalLabel = compactTotalHeld && displayedCompactTotal ? totalLineLabel(displayedCompactTotal) : null;
  // The label each held key last showed, so a line that leaves the board is
  // still named in the note (the key alone cannot be read back as a line).
  // Recorded while rendering (React's "information from previous renders"
  // pattern): the guard makes it a no-op once the label is stored.
  const [heldLabels, setHeldLabels] = useState<Readonly<Record<string, string>>>({});
  const unrecorded: Array<[string, string]> = [];
  if (compactSpreadKey != null && heldSpreadLabel != null && heldLabels[compactSpreadKey] !== heldSpreadLabel) {
    unrecorded.push([compactSpreadKey, heldSpreadLabel]);
  }
  if (compactTotalKey != null && heldTotalLabel != null && heldLabels[compactTotalKey] !== heldTotalLabel) {
    unrecorded.push([compactTotalKey, heldTotalLabel]);
  }
  if (unrecorded.length > 0) setHeldLabels({ ...heldLabels, ...Object.fromEntries(unrecorded) });
  const compactLineStatus: LineHoldEntry[] = [];
  if (compactSpreadMissing && compactSpreadKey != null) {
    compactLineStatus.push({
      kind: 'spread', label: heldLabels[compactSpreadKey] ?? '', state: compactSpreadMissingSettled ? 'missing' : 'finding',
    });
  }
  if (compactTotalMissing && compactTotalKey != null) {
    compactLineStatus.push({
      kind: 'total', label: heldLabels[compactTotalKey] ?? '', state: compactTotalMissingSettled ? 'missing' : 'finding',
    });
  }
  if (heldSpreadLabel != null) compactLineStatus.push({ kind: 'spread', label: heldSpreadLabel, state: 'held' });
  if (heldTotalLabel != null) compactLineStatus.push({ kind: 'total', label: heldTotalLabel, state: 'held' });
  /** H-4 — expanding or inspecting a row, or clicking its book, holds the line
   *  on screen when nothing is held yet (see SpreadTotalSection). Never on
   *  mount. */
  const holdShownCompactLine = (kind: 'spread' | 'total') => {
    if (kind === 'spread') {
      if (!compactSpreadHeld && displayedCompactSpread) setCompactSpreadKey(spreadLineKey(displayedCompactSpread));
    } else if (!compactTotalHeld && displayedCompactTotal) {
      setCompactTotalKey(totalLineKey(displayedCompactTotal));
    }
  };
  // The chip-strip compact view — spread/total only, since it needs a line
  // array to build chips from.
  const showCompactLines = compactAvailable && gameLinesViewMode === 'compact';
  // One section, one density preference — Brendan's call: whoever already chose
  // Compact gets Compact on every supported tab. Which RENDERER that means is
  // decided by the tab's shape, and there are three:
  //
  //   • spread/total → the EventRows chip-strip table (compactAvailable above)
  //   • Player Props / Events → the section's own table, with each value
  //     LADDER (a player+stat's 1+/2+/3+, a team's runs) collapsed to one row
  //     carrying a strip. Thresholds are points on a scale, exactly like a
  //     total's 8.5/9.5, and the analytics columns matter as much here as
  //     there — so these keep the full table (Dan, 2026-07-24).
  //   • Moneyline / BTTS → the outcome bands (#105). No scale between the
  //     outcomes, so nothing to collapse; density comes from dropping columns.
  //
  // Derived from the TAB, not from `!compactAvailable` — a spread tab whose
  // event hasn't loaded also has a null event, and it must not fall through to
  // another renderer.
  const isLineBearingTab =
    effectiveTab === 'lines' && (effectiveBetType === 'spread' || effectiveBetType === 'total');
  const isLadderTab = effectiveTab === 'props' || effectiveTab === 'events';
  const compactBandsAvailable = !isLineBearingTab && !isLadderTab;
  const showCompactBands = compactBandsAvailable && gameLinesViewMode === 'compact';
  const showCompactLadders = isLadderTab && gameLinesViewMode === 'compact';
  // Maker-fee rebate fraction for the bands' pill odds. GameRows computes
  // this internally from the row's OWN gameState — and every synth this
  // section builds is constructed `gameState: 'pre'` (period lines, BTTS,
  // prop rungs and event markets all are), so the pregame rate is what the
  // detailed rows use for these same rows. Matching it here keeps the two
  // views' American odds identical; if a synth ever carries a live state,
  // this has to become per-outcome.
  const bandRebateFraction = useMakerRebateActive()
    ? makerRebatePct({ isLive: false })
    : 0;

  // ── build the bet rows for the active bet-type × period ─────────────
  // Full-feed B: the OP bucket discriminator for the active period — the
  // vendor `period` string for period bets, 1 for full-game. A period g1
  // knows but OP has no vocabulary for simply never resolves (rows pin a
  // bucket that stays empty → "—").
  const opPeriodKey: number | string | undefined =
    effectivePeriod === 'full' ? 1 : OP_PERIOD_WIRE[effectivePeriod];
  /** Sign-resolve a spread row's away-perspective line to the wire's
   *  provider-home-handicap `points` FROM THE PROVIDER'S ORIENTATION —
   *  exactly as marketStore.getOpSpreadLineFor does for the full-game
   *  board: aligned (provider away = row away) ⇒ points = −line; swapped
   *  ⇒ points = +line. The orientation is read off the game's OP slice
   *  (`gameOpSlice`, the full-game moneyline — one orientation per game
   *  frame), never guessed.
   *
   *  It used to PROBE both signs and take whichever bucket existed. That
   *  is wrong whenever only one exists: +p and −p are two DIFFERENT
   *  markets (each team favoured by p), not two orientations of one. For
   *  the underdog's lines the correctly-signed bucket is usually absent,
   *  so the probe pinned the favourite's mirror and the cell printed the
   *  complement — Dan, NE@SEA 1H 2026-09-09: "NE wins by over 3.5" read
   *  57.2% (Kalshi 27¢) while "SEA wins by over 3.5" read 42.8%. A bucket
   *  that does not exist now renders "—", never another market's price. */
  const opSpreadBucket = (rowLine: number): OpBucketRef | undefined => {
    if (opPeriodKey == null || gameOpSlice == null) return undefined;
    const swapped = gameOpSlice.awayTeam === game.homeCode;
    return { gameKey: game.key, periodKey: opPeriodKey, marketType: 'spread', points: swapped ? rowLine : -rowLine };
  };
  /** Pin a Kalshi team-total rung to ITS team's OP team-total bucket.
   *  OP's `teamtotal-home` / `-away` suffix is the PROVIDER's orientation
   *  (OpOddsSlice doc: align via the slice, never inline), so the row's
   *  home/away is never used to choose a suffix — the store selector scans
   *  both buckets and returns the slice whose own named team is `teamCode`
   *  (exact declared strike, like every other OP line join). Resolved ⇒ pin
   *  the exact bucket; unresolved ⇒ no pin, and the row's OP cell stays
   *  the "—" every Game Props row rendered before the join. `periodKey` is
   *  1 for full game, else the vendor period string (OP_PERIOD_WIRE). */
  const opTeamTotalBucket = (
    teamCode: string,
    line: number,
    periodKey: number | string | undefined,
  ): OpBucketRef | undefined => {
    if (periodKey == null) return undefined;
    const sl = useMarketStore.getState().getOpTeamTotalLineFor(game.key, teamCode, line, periodKey);
    if (sl == null || typeof sl.market.points !== 'number') return undefined;
    return { gameKey: game.key, periodKey, marketType: sl.market.marketType, points: sl.market.points };
  };
  // Only full-game spread/total contracts use this exact line join. Period
  // lines remain separate until their native US binding is supported.
  const usKey = usGameKeyForBoardKey(game.key);
  const showUsLines = !!onUsPillClick && effectiveTab === 'lines' && effectivePeriod === 'full'
    && (effectiveBetType === 'spread' || effectiveBetType === 'total') && usKey !== null;
  const usCandidates = showUsLines ? useMarketStore.getState().getUsLineCandidates(usKey!) : { spreads: [], totals: [] };
  const rows: BetRow[] = [];
  if (effectiveBetType === 'spread' && event) {
    const built = buildSpreadBetRows(event);
    built.forEach((r, i) => {
      const line = event.spreads[i]?.line;
      if (typeof line === 'number') (r as BetRow).opBucket = opSpreadBucket(line);
    });
    rows.push(...built);
  } else if (effectiveBetType === 'total' && event) {
    const built = buildTotalBetRows(event);
    built.forEach((r, i) => {
      const line = event.totals[i]?.line;
      if (typeof line === 'number' && opPeriodKey != null) {
        (r as BetRow).opBucket = { gameKey: game.key, periodKey: opPeriodKey, marketType: 'total', points: line };
      }
    });
    rows.push(...built);
  } else if (effectiveBetType === 'moneyline' && moneyline) {
    const ml = moneyline;
    const tag = effectivePeriod; // '1H' | '2H' | 'F5' | 'full' — keys are period-scoped
    // Soccer's 'full' is the demoted regulation 1X2 — label it "90 min", not the
    // generic "full". Keys stay keyed on `tag` so they're unique per period.
    const tagLabel = effectivePeriod === 'full' ? '90 min' : effectivePeriod;
    // The soccer "90 min" line IS the demoted regulation 1X2 = full game
    // (periodTypeId 1), so it owns a real Unabated consensus — the SAME one the
    // round-robin moneyline pairs (knockout only moved it to Game Lines in
    // rendering, not in data). Pair it via the canonical game key (the synth row
    // keys carry a `|full-ml` suffix). Half/period moneylines have no
    // periodTypeId-1 consensus → leave undefined ("—").
    const mlConsensusKey = effectivePeriod === 'full' ? game.key : undefined;
    const aName = game.awayName || game.awayCode;
    const hName = game.homeName || game.homeCode;
    // Render the away/home pair when EITHER venue has both sides (soccer halves
    // can be Poly-only or Kalshi-only). Each venue's column is null when absent.
    const haveKalshiPair = !!(ml.away.kalshi && ml.home.kalshi);
    const havePolyPair = !!(ml.away.polymarket && ml.home.polymarket);
    if (haveKalshiPair || havePolyPair) {
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|${tag}-ml`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: `${aName} (${tagLabel})`,
          homeName: `${hName} (${tagLabel})`,
          kalshi: haveKalshiPair ? { away: ml.away.kalshi!, home: ml.home.kalshi! } : null,
          polymarket: havePolyPair ? { away: ml.away.polymarket!, home: ml.home.polymarket! } : null,
        },
        consensusKey: mlConsensusKey,
        // Full-feed B: the period 2-way moneyline (F5 is the priority
        // market). 'full' keeps the legacy FG read via consensusKey; a
        // 3-way period (soccer halves) pins a bucket OP never fills — "—".
        opBucket:
          effectivePeriod !== 'full' && opPeriodKey != null
            ? { gameKey: game.key, periodKey: opPeriodKey, marketType: 'moneyline' }
            : undefined,
      });
    }
    if (ml.tie.kalshi || ml.tie.polymarket) {
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|${tag}-ml-tie`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: `Tie (${tagLabel})`,
          homeName: `Tie (${tagLabel})`,
          kalshi: ml.tie.kalshi ? { away: ml.tie.kalshi, home: ml.tie.kalshi } : null,
          polymarket: ml.tie.polymarket ? { away: ml.tie.polymarket, home: ml.tie.polymarket } : null,
        },
        singleSide: 'away',
        // Same background as the Away/Home rows above — the three outcomes
        // read as one 3-way market, not two zebra-striped games.
        continuation: rows.length > 0,
        consensusKey: mlConsensusKey,
        // Draw row → reads the consensus' drawFairProbCents (only meaningful when
        // a consensus key is set, i.e. the "90 min" line).
        oddsOutcome: mlConsensusKey ? 'draw' : undefined,
      });
    }
  } else if (effectiveBetType === 'btts' && bttsBooks && (bttsBooks.kalshi || bttsBooks.polymarket)) {
    // Both teams to score — one binary (YES = both score). Full Game has no
    // period suffix; halves read "Both Teams To Score — 1st Half".
    const periodLabel = PERIODS.find((p) => p.id === effectivePeriod)?.label;
    const label =
      effectivePeriod === 'full' || !periodLabel
        ? 'Both Teams To Score'
        : `Both Teams To Score — ${periodLabel}`;
    rows.push({
      synth: {
        ...game,
        key: `${game.key}|${effectivePeriod}-btts`,
        gameState: 'pre',
        finalWinnerSide: null,
        awayName: label,
        homeName: label,
        kalshi: bttsBooks.kalshi ? { away: bttsBooks.kalshi, home: bttsBooks.kalshi } : null,
        polymarket: bttsBooks.polymarket ? { away: bttsBooks.polymarket, home: bttsBooks.polymarket } : null,
      },
      singleSide: 'away',
    });
  } else if (effectiveBetType === 'overtime' && nflOvertime) {
    // NFL overtime yes/no — a Game Lines chip here (Kalshi's own layout);
    // keyed on the real ticker so the parent's live-ticket resolver
    // (findLiveCfbEventSynth) re-finds it each frame like the CFB rows.
    rows.push({
      synth: buildCfbEventSynth(
        game,
        cfbEventSynthKey(game.key, nflOvertime.cls, nflOvertime.ticker),
        nflOvertime.label,
        cfbEventNoLabel(nflOvertime.label),
        nflOvertime.kalshi,
      ),
      singleSide: 'away',
    });
  } else if (effectiveBetType === 'race' && nflRaces.length > 0) {
    // NFL "Race to Points": one 3-way per points line (away / home / neither),
    // each leg its own YES market. A points line shares a zebra group like the
    // period moneyline's three outcomes.
    nflRaces.forEach((line, gi) => {
      line.outcomes.forEach((o, i) => {
        rows.push({
          synth: buildCfbEventSynth(game, cfbEventSynthKey(game.key, 'race-to', o.ticker), o.label, cfbEventNoLabel(o.label), o.kalshi),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: gi,
        });
      });
    });
  }

  // ── Player Props tab — the selected stat's per-player yes-ladders ──
  // Each rung is an independent binary bet ("F. Tatis 2+ Hits", YES resolves
  // at the threshold or more), rendered BTTS-style: one book per venue on both
  // sides of a singleSide row. HR/Ks rungs carry the client-side-joined Poly
  // book where Poly lists the threshold; everything else shows "—" in the P
  // columns. Either venue may be absent (unpaired rows render one-sided —
  // never fuzzy-matched into a pair). A player's rungs share a zebra group
  // (groupIndex) like the 3-way moneyline.
  if (effectiveTab === 'props' && activeStatGroup) {
    // Identity and wording come from `buildPlayerPropSummaries` (#490) — the
    // one description of a prop rung, shared with the day-wide prototype's
    // read-only row so neither surface can word or key a market differently.
    // This block still owns the TABLE shape: the synth's away/home slots carry
    // the outcome and its COMPLEMENT (#251, the order ticket reads them as its
    // YES/NO wording), and one book fills both sides of a singleSide row.
    // The synth shape lives in propEventRows so the parent's live resolver
    // (findLivePlayerPropSynth, Danielle R1 DNL-002) rebuilds byte-identical
    // rows each frame.
    buildPlayerPropSummaries(game, activeStatGroup).forEach((s) => {
      rows.push({
        synth: buildPlayerPropSynth(game, s),
        singleSide: 'away',
        continuation: s.rungIndex > 0,
        groupIndex: s.playerIndex,
        // op-player-props: pin the rung's OP/PNCL cell to core's
        // `player-prop` bucket for (this game, stat, WIRE playerKey, the
        // rung's line = threshold − 0.5). Only ladders whose identity came
        // from the wire carry a key (ticker-derived / Poly-only ladders pin
        // nothing and stay "—"); an unpaired or absent entry renders "—",
        // never a different market's price. YES ↔ OVER (positional).
        opBucket: s.opPlayerKey
          ? {
              gameKey: game.key,
              periodKey: 1,
              marketType: 'player-prop',
              points: s.threshold - 0.5,
              prop: { stat: s.stat, playerKey: s.opPlayerKey },
            }
          : undefined,
        // Same player + stat, thresholds ascending — the Compact bands
        // collapse these rungs into one selectable row (#141).
        ladder: {
          groupKey: s.groupKey,
          groupLabel: s.groupLabel,
          rungLabel: s.rungLabel,
        },
      });
    });
  }

  // ── CFB Events tab — the wire-classified game-event props ──────────
  // Every row is one Kalshi binary rendered BTTS-style (one book in both
  // slots of a singleSide row); the away/home slot names carry the YES
  // wording and its complement, which the order ticket reads as YES/NO
  // (#251). Labels are the venue's own `subtitle` — the only honest source
  // for "Georgia Tech wins 1H / Colorado wins game". Enum panes share a
  // zebra group per family; O/U ladders collapse in Compact (#141) like the
  // MLB team-total rungs. Kalshi-only: no Poly counterpart exists.
  if (effectiveTab === 'events' && cfbEvents) {
    let group = 0;
    const teamNameFor = (side: 'away' | 'home' | null, code: string | null) =>
      side === 'away' ? game.awayName || game.awayCode
        : side === 'home' ? game.homeName || game.homeCode
          : code ?? '';
    // Enum/binary rows: keyed on the REAL ticker so the parent's live-ticket
    // resolver (findLiveCfbEventSynth, DNL-001) re-finds them each frame.
    const single = (key: string, yes: string, no: string, kalshi: VenueBook) =>
      buildCfbEventSynth(game, key, yes, no, kalshi);
    for (const g of cfbEvents.enums) {
      g.outcomes.forEach((o, i) => {
        const label = cfbEnumRowLabel(g, o);
        rows.push({
          synth: single(cfbEventSynthKey(game.key, g.cls, o.ticker), label, cfbEventNoLabel(label), o.kalshi),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: group,
        });
      });
      group++;
    }
    for (const b of cfbEvents.binaries) {
      // NFL overtime is a Game Lines chip (Kalshi's layout) — not repeated here.
      if (isNfl && b.cls === 'overtime') continue;
      rows.push({
        synth: single(cfbEventSynthKey(game.key, b.cls, b.ticker), b.label, cfbEventNoLabel(b.label), b.kalshi),
        singleSide: 'away',
        groupIndex: group++,
      });
    }
    for (const l of cfbEvents.teamStats) {
      const teamBase = teamNameFor(l.teamSide, l.teamCode);
      // NFL team totals also list per period (KXNFL1HTEAMTOTAL): the period
      // tags the team so the 1H ladder cannot read as the full-game one. CFB
      // ladders are all full-game and keep their exact labels and keys.
      const periodTag = l.period === 'full' ? '' : ` (${l.period})`;
      const team = `${teamBase}${periodTag}`;
      const groupKey = cfbEventGroupKey(game.key, `teamstat-${l.stat}-${l.teamCode}${l.period === 'full' ? '' : `-${l.period}`}`);
      // OP team totals join the POINTS ladder only (`pts` = KX*TEAMTOTAL);
      // `ln.line` is already the half-point line, so it pairs at
      // points == ln.line. Period ladders (KXNFL1HTEAMTOTAL) pin the
      // vendor-period bucket via OP_PERIOD_WIRE.
      const ttPeriodKey: number | string | undefined =
        l.stat !== 'pts' ? undefined : l.period === 'full' ? 1 : OP_PERIOD_WIRE[l.period];
      l.lines.forEach((ln, i) => {
        rows.push({
          synth: single(
            `${groupKey}-${ln.line}`,
            `${team} Over ${ln.line} ${l.statLabel}`,
            `${team} Under ${ln.line} ${l.statLabel}`,
            ln.kalshi,
          ),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: group,
          ladder: { groupKey, groupLabel: `${team} ${l.statLabel}`, rungLabel: `${ln.line}` },
          opBucket: opTeamTotalBucket(l.teamCode, ln.line, ttPeriodKey),
        });
      });
      group++;
    }
    for (const t of cfbEvents.totals) {
      const groupKey = cfbEventGroupKey(game.key, t.cls);
      t.lines.forEach((ln, i) => {
        rows.push({
          synth: single(
            `${groupKey}-${ln.line}`,
            `Over ${ln.line} ${t.label}`,
            `Under ${ln.line} ${t.label}`,
            ln.kalshi,
          ),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: group,
          ladder: { groupKey, groupLabel: t.label, rungLabel: `${ln.line}` },
          // Full-feed B: OP carries these two families under the SAME names
          // (`total-touchdowns` / `total-fieldgoals`, full game) — pin the
          // row's OP/PNCL cell to the bucket at this line.
          opBucket: { gameKey: game.key, periodKey: 1, marketType: t.cls, points: ln.line },
        });
      });
      group++;
    }
    // NFL "both teams score N+" (class both-teams-points, pending core deploy):
    // an O/U-shaped ladder whose line is the WHOLE number the wire carries.
    if (cfbEvents.bothTeamsPoints.length > 0) {
      const groupKey = cfbEventGroupKey(game.key, 'both-teams-points');
      cfbEvents.bothTeamsPoints.forEach((ln, i) => {
        rows.push({
          synth: single(
            `${groupKey}-${ln.line}`,
            `${BOTH_TEAMS_POINTS_LABEL} ${ln.line}+`,
            `Not both teams ${ln.line}+`,
            ln.kalshi,
          ),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: group,
          ladder: { groupKey, groupLabel: BOTH_TEAMS_POINTS_LABEL, rungLabel: `${ln.line}+` },
        });
      });
      group++;
    }
  }

  // ── UFC Fight Props tab (#848) ─────────────────────────────────────
  // One zebra group per family (per fighter where the family is sided), rows
  // already ordered by the selector: total rounds by round, method by
  // KO/TKO/DQ → Submission → Decision, round of victory by round. Each row is
  // one Kalshi binary rendered BTTS-style; the synth's two name slots carry
  // the YES proposition and its negation, which the ticket reads as YES/NO.
  // Plain rows, not collapsed ladders: the ticket names the exact proposition
  // ("Fight ends before round 3 — YES") rather than a rung chip.
  if (effectiveTab === 'events' && ufcProps) {
    ufcProps.groups.forEach((g, gi) => {
      g.rows.forEach((r, i) => {
        rows.push({
          synth: buildUfcFightPropSynth(game, r),
          singleSide: 'away',
          continuation: i > 0,
          groupIndex: gi,
        });
      });
    });
  }
  // Polymarket fight props, below the Kalshi families: one Poly binary per
  // row, the Gamma question as its YES proposition (Over on a rounds total),
  // grouped and ordered by the selector (distance, method, rounds, round).
  if (effectiveTab === 'events' && polyFightProps.length > 0) {
    let gi = ufcProps?.groups.length ?? 0;
    let lastGroup: PolyFightPropRow['group'] | null = null;
    for (const fp of polyFightProps) {
      if (lastGroup !== null && fp.group !== lastGroup) gi++;
      const continuation = fp.group === lastGroup;
      lastGroup = fp.group;
      rows.push({
        synth: buildPolyFightPropSynth(game, fp),
        singleSide: 'away',
        continuation,
        groupIndex: gi,
      });
    }
  }

  // ── MLB Events tab — YRFI/NRFI, per-team Team Totals, Extra Innings ──
  // RFI + extras are two-venue binaries since poly-props-events; the Poly
  // book attaches at the SAME polarity (YES = run scores / YES = extras) —
  // marketStore joins with no flip. Team totals stay Kalshi-only.
  if (effectiveTab === 'events' && eventMarkets) {
    let group = 0;
    if (eventMarkets.rfi) {
      const { kalshi: rfiK, polymarket: rfiP } = eventMarkets.rfi;
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|event-rfi`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: 'Run scored in 1st inning (YRFI)',
          homeName: 'Run scored in 1st inning (YRFI)',
          kalshi: rfiK ? { away: rfiK, home: rfiK } : null,
          polymarket: rfiP ? { away: rfiP, home: rfiP } : null,
        },
        singleSide: 'away',
        groupIndex: group++,
      });
    }
    for (const tt of eventMarkets.teamTotals) {
      tt.rungs.forEach((rung, rIdx) => {
        const label = `${tt.teamName} ${rung.threshold}+ runs`;
        rows.push({
          synth: {
            ...game,
            key: `${game.key}|event-teamtotal-${tt.teamCode}-${rung.threshold}`,
            gameState: 'pre',
            finalWinnerSide: null,
            awayName: label,
            homeName: label,
            kalshi: rung.kalshi ? { away: rung.kalshi, home: rung.kalshi } : null,
            polymarket: null,
          },
          singleSide: 'away',
          continuation: rIdx > 0,
          groupIndex: group,
          // One team's runs ladder — collapse the rungs in Compact (#141).
          ladder: {
            groupKey: `${game.key}|event-teamtotal-${tt.teamCode}`,
            groupLabel: `${tt.teamName} runs`,
            rungLabel: `${rung.threshold}+`,
          },
          // A "N+" rung resolves YES at N or more runs — the Over (N − 0.5)
          // line (describeMarket's mlb team-total mapping; hydracore's
          // pairedGate keys the same floorStrike). Full game only.
          opBucket: opTeamTotalBucket(tt.teamCode, rung.threshold - 0.5, 1),
        });
      });
      group++;
    }
    if (eventMarkets.extras) {
      const { kalshi: exK, polymarket: exP } = eventMarkets.extras;
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|event-extras`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: 'Extra innings played',
          homeName: 'Extra innings played',
          kalshi: exK ? { away: exK, home: exK } : null,
          polymarket: exP ? { away: exP, home: exP } : null,
        },
        singleSide: 'away',
        groupIndex: group++,
      });
    }
  }

  // ── OddsPapi bucket per seeded row ──────────────────────────────────
  // GameDetail's odds table follows the line the ticket is on. Every row here
  // already pins its OP bucket; the full-game moneyline row reads the game's
  // full-game moneyline bucket, as its OP cell does through consensusKey. A
  // ticket seeded from a row carries that row's bucket.
  const opBucketBySynthKey = new Map<string, OpBucketRef>();
  for (const r of rows) {
    const bucket: OpBucketRef | undefined = r.opBucket
      ?? (r.consensusKey ? { gameKey: game.key, periodKey: 1, marketType: 'moneyline' } : undefined);
    if (bucket) opBucketBySynthKey.set(r.synth.key, bucket);
  }
  const seedTicket = (ctx: TradeTicketContext) =>
    onPillClick({ ...ctx, opBucket: opBucketBySynthKey.get(ctx.game.key) ?? null });

  const seedUsTicket = (ctx: TradeTicketContext) =>
    onUsPillClick?.({ ...ctx, opBucket: opBucketBySynthKey.get(ctx.game.key) ?? null });

  // ── Deep-link focus (Positions page ?ticker=&side=) ─────────────────
  // A period bet's row lives here, not in the full-game sections, so Game
  // Lines claims the focus. Phase 1: point the bet-type + period tabs at the
  // ticker. Phase 2: once that line's row is built, expand it + seed the
  // trade ticket. GameDetail skips its full-game default seed for period
  // tickers so this isn't clobbered.
  const pendingFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusTicker) return;
    const ft = focusTicker.toUpperCase();
    let bt: BetType | null = null;
    let per: Period | null = null;
    // NBA/WNBA period markets: KX(W)NBA{1H|1-4Q}{SPREAD|TOTAL|WINNER}.
    const nba = ft.match(/^KXW?NBA(1H|[1-4]Q)(SPREAD|TOTAL|WINNER)/);
    // Soccer Game Lines: KX{WC|UECL|INTLFRIENDLY}{1H|2H}?{SPREAD|TOTAL|BTTS}?.
    // A bare period token (KXWC1H) is the moneyline; BTTS may be full game.
    // Full-game spread/total (no period, not BTTS) is owned by the top sections.
    const soc = ft.match(/^KX(?:WC|UECL|INTLFRIENDLY)(1H|2H)?(SPREAD|TOTAL|BTTS)?(?=-)/);
    // MLB First 5 Innings: KXMLBF5{SPREAD|TOTAL}? — bare KXMLBF5 is the 3-way
    // moneyline (no GAME suffix), SPREAD/TOTAL append. Full-game MLB (no F5) is
    // owned by the top sections.
    const mlbF5 = ft.match(/^KXMLBF5(SPREAD|TOTAL)?(?=-)/);
    // NFL halves: KXNFL{1H|2H}{SPREAD|TOTAL}? — a bare KXNFL1H/KXNFL2H is the
    // half moneyline (no WINNER suffix), as KXMLBF5 is for innings. Full-game
    // NFL (no half token) is owned by the top sections. The `-` lookahead keeps
    // the unevidenced KXNFL1HWINNER/KXNFL2HWINNER families fail-closed here,
    // exactly as the parser does.
    // Quarters and the quarter BTTS joined the halves (nfl-game-lines-props).
    const nflHalf = ft.match(/^KXNFL(1H|2H|[1-4]Q)(SPREAD|TOTAL|BTTS)?(?=-)/);
    // CFB periods (cfb-game-props): KXNCAAF{1H|2H|1Q–4Q}{SPREAD|TOTAL|BTTS|
    // WINNER}? — a bare period token is the 3-way winner. Full-game CFB is
    // owned by the top sections.
    const cfbPeriod = ft.match(/^KXNCAAF(1H|2H|[1-4]Q)(SPREAD|TOTAL|BTTS|WINNER)?(?=-)/);
    // NHL periods (#1218): KXNHL{1P|2P|3P}{SPREAD|TOTAL|BTTS}? — a bare period
    // token is the 3-way winner.
    const nhlPeriod = ft.match(/^KXNHL([1-3]P)(SPREAD|TOTAL|BTTS)?(?=-)/);
    // CFB game-event props: membership in the Events tab's own ticker set —
    // read from the store at effect time (NOT a dep: the memo re-derives on
    // every feed tick and would re-seed the focus each time). No ticker
    // grammar: the tab is classified by the wire, so is its deep link.
    // UFC Fight Props (#848): membership in the selector's admitted set.
    if ((isUfc && useMarketStore.getState().getUfcFightProps(game.key)?.tickers.has(ft))
      // Poly fight props: `ft` is uppercased, the PM slug is stored raw.
      || (fightPolyTicker && ft.startsWith(`${fightPolyTicker.toUpperCase()}-`)
        && useMarketStore.getState().getPolyFightProps(fightPolyTicker).some((fp) => fp.ticker.toUpperCase() === ft))) {
      setTab('events');
      if (focusSide) setMarketSide(focusSide);
      pendingFocusRef.current = ft;
      return;
    }
    const footballEv = hasWireEvents ? useMarketStore.getState().getCfbEventMarkets(game.key) : null;
    if (footballEv?.tickers.has(ft)) {
      // NFL overtime / race-to legs are Game Lines chips, not Game Props rows.
      const isOt = isNfl && footballEv.binaries.some((b) => b.cls === 'overtime' && b.ticker === ft);
      const isRace = isNfl && footballEv.races.some((l) => l.outcomes.some((o) => o.ticker === ft));
      if (isOt || isRace) {
        setTab('lines');
        setBetType(isOt ? 'overtime' : 'race');
      } else {
        setTab('events');
      }
      if (focusSide) setMarketSide(focusSide);
      pendingFocusRef.current = ft;
      return;
    }
    // Wire-sport player props (NFL, NHL, NBA): the stat ladder's tab + chip,
    // by the ticker's exact series (lib/sport/propStat) so a link that lands
    // before its frame still opens the right tab; the row scan below matches
    // the exact ticker. A series of another sport is not this game's.
    const seriesProp = propStatOfKalshiSeries(ft);
    if (seriesProp && propStatSport(seriesProp) === game.sport) {
      setTab('props');
      setPropStat(seriesProp);
      if (focusSide) setMarketSide(focusSide);
      pendingFocusRef.current = ft;
      return;
    }
    // MLB player-prop / event-market tickers — Kalshi series (mlb-props-
    // events) OR Poly PM slugs (poly-props-events, DNL-G4): switch to their
    // tab (+ stat chip); the row-scan effect below expands the row and seeds
    // the ticket once the ladder is built. The Poly classifier gets the RAW
    // focusTicker (its slug grammar is lowercase; `ft` is uppercased).
    const mlbExt = isMlb ? classifyMlbExtendedTicker(ft) : null;
    const polyExt = !mlbExt && isMlb ? classifyPolyExtendedTicker(focusTicker) : null;
    const ext = mlbExt ?? polyExt;
    if (ext) {
      setTab(ext.kind === 'prop' ? 'props' : 'events');
      if (ext.kind === 'prop') setPropStat(ext.stat);
      if (focusSide) setMarketSide(focusSide);
      pendingFocusRef.current = ft;
      return;
    }
    if (nba) {
      bt = nba[2] === 'SPREAD' ? 'spread' : nba[2] === 'TOTAL' ? 'total' : 'moneyline';
      per = nba[1] as Period;
    } else if (soc && (soc[1] || soc[2] === 'BTTS')) {
      bt = soc[2] === 'SPREAD' ? 'spread' : soc[2] === 'TOTAL' ? 'total' : soc[2] === 'BTTS' ? 'btts' : 'moneyline';
      per = (soc[1] as Period) ?? 'full';
    } else if (mlbF5) {
      bt = mlbF5[1] === 'SPREAD' ? 'spread' : mlbF5[1] === 'TOTAL' ? 'total' : 'moneyline';
      per = 'F5';
    } else if (nflHalf) {
      bt = nflHalf[2] === 'SPREAD' ? 'spread' : nflHalf[2] === 'TOTAL' ? 'total' : nflHalf[2] === 'BTTS' ? 'btts' : 'moneyline';
      per = nflHalf[1] as Period;
    } else if (cfbPeriod) {
      bt = cfbPeriod[2] === 'SPREAD' ? 'spread' : cfbPeriod[2] === 'TOTAL' ? 'total' : cfbPeriod[2] === 'BTTS' ? 'btts' : 'moneyline';
      per = cfbPeriod[1] as Period;
    } else if (nhlPeriod) {
      bt = nhlPeriod[2] === 'SPREAD' ? 'spread' : nhlPeriod[2] === 'TOTAL' ? 'total' : nhlPeriod[2] === 'BTTS' ? 'btts' : 'moneyline';
      per = nhlPeriod[1] as Period;
    }
    if (!bt || !per) return; // full-game ticker — the top sections own it
    setTab('lines'); // period bets live on the Game Lines tab (no-op for non-MLB)
    setBetType(bt);
    setSelectedPeriod(per);
    if (focusSide) setMarketSide(focusSide);
    pendingFocusRef.current = ft;
  }, [focusTicker, focusSide, isMlb, isCfb, isNfl, hasWireEvents, isUfc, fightPolyTicker, game.key, game.sport, setTab, setBetType, setSelectedPeriod, setMarketSide, setPropStat]);
  useEffect(() => {
    const ft = pendingFocusRef.current;
    if (!ft) return;
    // Case-insensitive book match across BOTH venues (DNL-G4): Kalshi
    // tickers are uppercase already; PM tickers are stored raw (lowercase
    // slug body) while `ft` was uppercased by the tab-switch effect.
    const matches = (b: VenueBook | null | undefined) =>
      !!b && b.marketTicker.toUpperCase() === ft;
    const target = rows.find(
      (r) =>
        matches(r.synth.kalshi?.away) || matches(r.synth.kalshi?.home)
        || matches(r.synth.polymarket?.away) || matches(r.synth.polymarket?.home),
    );
    if (!target) return; // target period's rows not built yet — re-runs when they are
    pendingFocusRef.current = null;
    const kalshiHit = matches(target.synth.kalshi?.away) || matches(target.synth.kalshi?.home);
    const side: TeamSide = kalshiHit
      ? matches(target.synth.kalshi?.home) && !matches(target.synth.kalshi?.away)
        ? 'home'
        : 'away'
      : matches(target.synth.polymarket?.home) && !matches(target.synth.polymarket?.away)
        ? 'home'
        : 'away';
    setExpanded({ key: target.synth.key, side });
    const focusedLineIdx = rows.indexOf(target);
    const focusedLine = effectiveBetType === 'spread'
      ? event?.spreads[focusedLineIdx]
      : effectiveBetType === 'total'
        ? event?.totals[focusedLineIdx]
        : null;
    onPanelLineChange?.(
      effectiveTab === 'lines' && focusedLine
        ? {
            kind: effectiveBetType as 'spread' | 'total',
            key: effectiveBetType === 'spread'
              ? spreadLineKey(focusedLine as SpreadLine)
              : totalLineKey(focusedLine as TotalLine),
          }
        : null,
    );
    seedTicket({
      game: target.synth,
      teamSide: side,
      venue: kalshiHit ? 'kalshi' : 'polymarket',
      initialOrderSide: focusSide ?? marketSide,
      postOnly: priceMode === 'maker',
    });
    // Deep-linked to a grouped prop/event rung: select THAT rung in the
    // collapsed compact band (+ expand it + mark it as owning the ticket) so the
    // band and the just-seeded ticket agree on the market — preserving exact
    // selected-market identity (#141 review). No-op for non-ladder targets. This
    // runs after the structural-reset effect (defined above), so its selection
    // survives the reset that fires on the same tab/stat change.
    const rungSel = resolveFocusRungSelection(rows, ft);
    if (rungSel) {
      setRungKeyByGroup((prev) => new Map(prev).set(rungSel.groupKey, rungSel.chipKey));
      setExpandedBandKey(rungSel.groupKey);
      setOpenBandTicket({ key: rungSel.groupKey, venue: rungSel.venue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Visibility resolved up front so the 1H moneyline pair + Tie can
  // coordinate as ONE selection group: whichever of them is expanded, the
  // blue box wraps both (group top rail on the pair, bottom rail on the
  // Tie). Spread/total rows are independent bets and never group.
  const visibleRows = rows.map((r) => {
    const settled = rowIsSettled(r.synth);
    // Keep a deep-link target visible even if a filter would hide it, so its
    // rung stays selectable and the collapsed band can't disagree with the
    // seeded ticket (the Hide-0-Vol filtered-target gap, #141 review). This flag
    // feeds `visible`, which drives BOTH the compact bands and the Detailed
    // table below, and it persists while `focusTicker` is set — so a linked
    // 0-vol / settled row also stays shown in Detailed and survives later filter
    // toggles for that session (a deliberate widen of the deep-link, not just a
    // band concern).
    const isFocus = focusTicker != null && synthMatchesTicker(r.synth, focusTicker);
    const visible = bandRowVisible({
      hasVolume: rowHasVolume(r.synth),
      settled,
      hideNoVol,
      hideSettled,
      isFocus,
    });
    return { ...r, settled, visible };
  });
  const mlPair = effectiveTab === 'lines' && effectiveBetType === 'moneyline' ? visibleRows.find((r) => !r.continuation) : undefined;
  const mlTie = effectiveTab === 'lines' && effectiveBetType === 'moneyline' ? visibleRows.find((r) => r.continuation) : undefined;
  const mlPairExpanded = !!mlPair?.visible && expanded?.key === mlPair.synth.key;
  const mlTieExpanded = !!mlTie?.visible && expanded?.key === mlTie.synth.key;

  // Grouped (Props/Events) ladder rows: resolve `continuation` against
  // VISIBILITY — a rung whose ladder predecessor is hidden (0-vol / settled)
  // must not render as a continuation of the previous group's rows.
  let _prevVisibleGroup: number | null = null;
  const groupedContinuation = visibleRows.map((r) => {
    if (!r.visible) return false;
    const cont = r.groupIndex != null && !!r.continuation && _prevVisibleGroup === r.groupIndex;
    _prevVisibleGroup = r.groupIndex ?? null;
    return cont;
  });

  // ── Compact renderers, from the SAME rows the detailed table renders ──
  // Both builders are pure + unit-tested, and both honour the same 0-vol /
  // settled visibility the table applies, so the filter chips keep working.
  //
  // Ladder tabs (Props / Events): one table row per ladder, thresholds on the
  // strip. The rung synths feed the ticket-follows-the-arrow handler
  // (handleSelectRung).
  let ladderTableRows: ReturnType<typeof buildLadderTableRows>['rows'] = [];
  // Per-ladder rung synths (groupKey → chip id → synth). A render-local map, not
  // a ref: handleSelectRung closes over THIS render's value, so the rung the
  // user clicks maps 1:1 to the synths on screen — no ref write during render.
  let bandRungSynths = new Map<string, Map<string, { synth: GameRow; teamSide: TeamSide }>>();
  // Built on BOTH view modes (not just Compact): the panel-ownership effect
  // below resolves panelLadderKey against these rows, and since #149 a Detailed
  // prop row's open also claims the panel — without this the Detailed claim
  // would find no ladder and the panel would fall back to a click-time
  // snapshot that never ticks.
  if (isLadderTab) {
    const built = buildLadderTableRows(visibleRows, rungKeyByGroup);
    ladderTableRows = built.rows;
    bandRungSynths = built.rungTargets;
  }
  // #1112 — a Props / Events tab with no Polymarket market drops the four
  // Poly columns. Read off EVERY row of the tab + stat chip, before the
  // 0-Vol / settled filters and not the compact ladder's selected rung, so
  // it follows market identity only: a price tick, a freshness change, a
  // filter toggle or a rung pick never flips it. The Lines tab keeps them.
  // The same tabs also hold their columns still when a row expands
  // (GameRows' holdColumnsOnExpand).
  const hidePolyColumns = isLadderTab && !rows.some((r) => r.synth.polymarket != null);
  // Outcome tabs (Moneyline / BTTS): one band per named outcome.
  let bandOutcomes: CompactOutcome[] = [];
  let bandTargets = new Map<string, { synth: GameRow; teamSide: TeamSide }>();
  if (showCompactBands) {
    const built = buildBandOutcomes(visibleRows);
    bandOutcomes = built.outcomes;
    bandTargets = built.targets;
  }

  const handleBandTrade = (
    outcome: CompactOutcome,
    venue: 'kalshi' | 'polymarket',
    side: MarketSide,
    cents: number | null,
  ) => {
    const target = bandTargets.get(outcome.key);
    if (!target) return;
    // Mirrors the detailed rows' onTradeClick exactly: flip the table's
    // YES/NO to the clicked side, clear the panel's bet-line context (these
    // tabs have no spread/total line to point it at — leaving a stale one is
    // what opens the ticket on the wrong market), then seed the ticket.
    setMarketSide(side);
    // Moneyline/BTTS bands are outside the LT2 coverage boundary — a priced
    // click while armed leaves the hot path, so it disarms (V1 boundary).
    if (cents != null && useOneClickStore.getState().armed) {
      useOneClickStore.getState().disarm('This market uses the order ticket. Re-arm to return to supported one-click markets.');
    }
    onPanelLineChange?.(null);
    seedTicket({
      game: target.synth,
      teamSide: target.teamSide,
      venue,
      prefillPrice: cents ?? undefined,
      initialOrderSide: side,
      postOnly: false,
    });
    // Record which band + venue now owns the ticket, so arrowing this band's
    // rung can move the ticket with it (below). A band is never a ladder, so
    // the panel goes back to its plain layout.
    setOpenBandTicket({ key: outcome.key, venue });
    setPanelLadderKey(null);
  };

  /** Seed the ticket from a collapsed LADDER row's pill. Same wiring as
   *  handleBandTrade, but the trade routes with the SELECTED RUNG's own synth
   *  (row.target) rather than the display row, so the ticket names the actual
   *  threshold market ("2+ Hits") and carries its ticker. */
  const handleLadderTrade = (
    rowKey: string,
    target: { synth: GameRow; teamSide: TeamSide },
    side: TeamSide,
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => {
    if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
    // Prop/event ladders are outside the LT2 coverage boundary — a priced
    // click while armed leaves the hot path, so it disarms (V1 boundary).
    if (opts?.priceCents != null && useOneClickStore.getState().armed) {
      useOneClickStore.getState().disarm('This market uses the order ticket. Re-arm to return to supported one-click markets.');
    }
    // These tabs have no spread/total line for the panel to point at; leaving a
    // stale one is what opens the ticket on the wrong market.
    onPanelLineChange?.(null);
    seedTicket({
      game: target.synth,
      teamSide: side,
      venue,
      prefillPrice: opts?.priceCents,
      initialOrderSide: opts?.initialOrderSide,
      postOnly: opts?.postOnly ?? false,
    });
    // Record which ladder + venue now owns the ticket, and hand the panel this
    // ladder so its order entry carries the threshold strip too (below).
    setOpenBandTicket({ key: rowKey, venue });
    setPanelLadderKey(rowKey);
    if (effectiveTab === 'props' && twinSeriesProps) {
      const bookKey = chipKeyFor(target.synth.kalshi?.[side], target.synth.polymarket?.[side], `${target.synth.key}|${side}`);
      setRungKeyByGroup((prev) => new Map(prev).set(rowKey, bookKey));
    }
    // #183: a PROP pill also opens the ladder's inline order book (rowKey is the
    // ladder's compact key — the same state its row/player-name action toggles).
    // Props only: Events pill behavior is unchanged (a pure set here, never a
    // toggle, so repeated activation keeps the book open). This handler serves
    // both ladder tabs, so gate on the active tab.
    if (effectiveTab === 'props') setExpandedBandKey(rowKey);
  };

  /** Switch a ladder row's rung. Updates the row AND re-seeds the order form to
   *  that rung's market — including when the form is currently on a DIFFERENT
   *  ladder: clicking Karros 2+ while the ticket sits on Yelich 1+ moves the
   *  form to Karros 2+ (Dan, 2026-07-24). A threshold click is a market
   *  selection, so making it a no-op unless you'd already clicked a pill is the
   *  divergence the Game Total strip never has. */
  const handleSelectRung = (outcomeKey: string, rungKey: string) => {
    setRungKeyByGroup((prev) => {
      const next = new Map(prev);
      next.set(outcomeKey, rungKey);
      return next;
    });
    const rung = bandRungSynths.get(outcomeKey)?.get(rungKey);
    if (!rung) return;
    // Venue via the shared rule (propSelection) — the prop-row seed uses the
    // same one. Sticky only if this ladder owns the ticket AND that venue lists
    // the new rung; a bare sticky venue left a dead Poly form when the next
    // rung was Kalshi-only (and the converse).
    const resolved = resolveRungVenue({
      target: rung,
      ticketKey: outcomeKey,
      openTicket: openBandTicket,
    });
    if (!resolved) return;
    const { venue } = resolved;
    setOpenBandTicket({ key: outcomeKey, venue });
    setPanelLadderKey(outcomeKey);
    onPanelLineChange?.(null);
    seedTicket({
      game: rung.synth,
      teamSide: rung.teamSide,
      venue,
      initialOrderSide: marketSide,
      postOnly: false,
    });
  };

  // Emit whatever currently owns the right-side panel as a bet-line context —
  // same mechanism SpreadTotalSection uses, so the order entry reads
  // identically (a bet description + line strip with a single YES/NO, not a
  // team-vs-team header). Declared here, after the rows and handlers it reads,
  // so nothing is referenced before it exists. Re-runs as the data ticks so
  // the panel stays live. Null on a moneyline/BTTS selection (team-style
  // panel).
  useEffect(() => {
    if (!onActiveLineChange) return;
    // A collapsed LADDER owns the panel: hand it the ladder's own threshold
    // strip, so rungs switch from the order entry exactly as spread/total lines
    // do. A ladder that dropped to one visible rung has no strip — fall through
    // to the plain panel rather than showing a one-chip selector.
    if (panelLadderKey) {
      const lad = ladderTableRows.find((r) => r.key === panelLadderKey);
      const selected = rungKeyByGroup.get(panelLadderKey);
      const nativeSelectionMatches = !twinSeriesProps || effectiveTab !== 'props' || !selected || (lad &&
        chipKeyFor(lad.target.synth.kalshi?.away, lad.target.synth.polymarket?.away, `${lad.target.synth.key}|away`) === selected);
      const context = lad && nativeSelectionMatches
        ? buildLadderBetLineContext(lad, bandRungSynths.get(panelLadderKey), (chipKey) =>
            handleSelectRung(panelLadderKey, chipKey),
          )
        : null;
      onActiveLineChange(context && lad ? { game: lad.target.synth, context, opBucket: lad.target.opBucket ?? null } : null);
      return;
    }
    if (!panelLine || !event) {
      onActiveLineChange(null);
      return;
    }
    if (panelLine.kind === 'spread') {
      const selectedIdx = resolveLineIndexByKey(
        event.spreads,
        panelLine.key,
        spreadLineKey,
        compactDefaultSpreadIdx,
      );
      const line = event.spreads[selectedIdx];
      if (!line) { onActiveLineChange(null); return; }
      const synth = synthesizeFromSpread(event, line);
      onActiveLineChange({
        game: synth,
        opBucket: opBucketBySynthKey.get(synth.key) ?? null,
        context: buildBetLineContext({
          kind: 'spread', event, lines: event.spreads, selectedIdx,
          onSelectIdx: (i) => {
            const next = event.spreads[i];
            if (next) onPanelLineChange?.({ kind: 'spread', key: spreadLineKey(next) });
          },
        }),
      });
    } else {
      const selectedIdx = resolveLineIndexByKey(
        event.totals,
        panelLine.key,
        totalLineKey,
        compactDefaultTotalIdx,
      );
      const line = event.totals[selectedIdx];
      if (!line) { onActiveLineChange(null); return; }
      const synth = synthesizeFromTotal(event, line);
      onActiveLineChange({
        game: synth,
        opBucket: opBucketBySynthKey.get(synth.key) ?? null,
        context: buildBetLineContext({
          kind: 'total', event, lines: event.totals, selectedIdx,
          onSelectIdx: (i) => {
            const next = event.totals[i];
            if (next) onPanelLineChange?.({ kind: 'total', key: totalLineKey(next) });
          },
        }),
      });
    }
    // The ladder branch reads render-local row/handler values by design (they
    // are rebuilt every render); `version` is what makes it re-emit on a tick.
    //
    // `rungKeyByGroup` is load-bearing, not cosmetic: a rung switch INSIDE one
    // ladder leaves panelLadderKey and version untouched, so without it the
    // panel kept rung A's books and order identity while remounting under B —
    // until the next feed tick happened to re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelLadderKey, rungKeyByGroup, version, panelLine, event, onActiveLineChange, onPanelLineChange]);

  if (!hasLines && !hasProps && !hasEvents && !focusUsMarket) return null;

  return (
    <section data-view-anchor="game-lines" className="bg-card border-border rounded-lg border">
      {focusUsMarket && !usCatalogue.markets.some(m => m.marketSlug === focusUsMarket) && (
        <p role="status" className="border-border border-b px-4 py-2 text-xs text-muted-foreground">
          {usCatalogue.status === 'loading' ? 'Loading the requested US contract…'
            : 'The requested US contract is unavailable. No substitute was selected.'}
        </p>
      )}
      {/* Title (MLB: the Game Lines | Player Props | Events selector; other
          sports keep the static title) + section toolbar (Maker/Taker/Market +
          YES/NO). Tabs with no ingested markets render disabled — the designed
          empty-state degrade while the feed side is dark / lineups unposted. */}
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        {hasTabs ? (
          <div className="flex items-center gap-4">
            {SECTION_TABS.filter((t) =>
              isFightPropsTab
                // A boxing bout with Poly props keeps any Game Lines it has.
                ? t.id === 'events' || (t.id === 'lines' && !isUfc && hasLines)
                : t.id === 'lines'
                  || (t.id === 'props' ? propSport != null || isUsPlayerSport : isMlb || isCfb || isNfl || isNhl),
            ).map((t) => {
              const available = t.id === 'lines' ? hasLines : t.id === 'props' ? hasProps : hasEvents;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={!available}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'text-lg font-bold transition-colors',
                    effectiveTab === t.id
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                    !available && 'text-muted-foreground/40 hover:text-muted-foreground/40 cursor-not-allowed',
                  )}
                >
                  {isFightPropsTab && t.id === 'events' ? UFC_FIGHT_PROPS_TAB_LABEL : t.label}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-foreground text-lg font-bold">Game Lines</span>
        )}
        {/* One trailing control cluster: the toolbar, then a hairline, then
            Reset view at the right edge beside the choices it resets. Under
            width pressure the whole cluster wraps beneath the title as one
            unit, so the controls never split across lines. */}
        <div className="flex shrink-0 items-center gap-2">
          <SectionToolbar
            priceMode={priceMode}
            onPriceMode={setPriceMode}
            marketSide={marketSide}
            onMarketSide={setMarketSide}
            viewMode={
              compactAvailable || compactBandsAvailable || isLadderTab
                ? gameLinesViewMode
                : undefined
            }
            onViewMode={
              compactAvailable || compactBandsAvailable || isLadderTab
                ? setGameLinesViewMode
                : undefined
            }
            showPolyOnlyLines={lt2Access && !butterflyOn && event != null ? showPolyOnlyLines : undefined}
            onShowPolyOnlyLines={lt2Access && !butterflyOn && event != null ? setShowPolyOnlyLines : undefined}
          />
          <span aria-hidden="true" className="bg-border/60 h-4 w-px" />
          <WorkingViewReset
            label="Game Lines"
            scope="price mode, YES/NO side, bet type, period, tab, prop stat, and the 0-vol / settled filters"
            reset={reading.reset}
            undo={reading.undo}
            canUndo={reading.canUndo}
          />
        </div>
      </div>

      {/* Bet-type + period selectors — button style (matches the Maker/Taker
          toolbar). Only AVAILABLE options render: bet types with markets, and
          for the active bet type the periods that have data (so Moneyline shows
          just 1H, no quarters; Overtime / 2nd Half never appear). */}
      <div className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-2">
        {effectiveTab === 'lines' && (
          <ChipGroup>
            {BET_TYPES.filter((bt) => availableBets.includes(bt.id)).map((bt) => (
              <Chip key={bt.id} active={effectiveBetType === bt.id} onClick={() => setBetType(bt.id)}>
                {bt.label}
              </Chip>
            ))}
          </ChipGroup>
        )}
        {effectiveTab === 'lines' && availForBet.length > 0 && (
          <ChipGroup>
            {periodChips.map((p) => (
              <Chip key={p.id} active={effectivePeriod === p.id} onClick={() => setSelectedPeriod(p.id)}>
                {/* Soccer's full game is 90 minutes of regulation — relabel the
                    shared 'Full Game' entry per-sport (don't edit PERIODS, which
                    BTTS also reads). Other sports keep "Full Game". */}
                {game.sport === 'soccer' && p.id === 'full' ? '90 min' : p.label}
              </Chip>
            ))}
          </ChipGroup>
        )}
        {/* Player Props stat chips — data-driven like the period chips (only
            stats with ingested markets render; the selectors return them in
            the sport's canonical stat order — lib/sport/propStat). */}
        {effectiveTab === 'props' && playerProps.length > 0 && (
          <ChipGroup>
            {playerProps.map((g) => (
              <Chip
                key={g.stat}
                active={activeStatGroup?.stat === g.stat}
                onClick={() => setPropStat(g.stat)}
              >
                {PROP_STAT_LABELS[g.stat]}
              </Chip>
            ))}
          </ChipGroup>
        )}
        {/* Kept visible in the BANDS compact view: those tabs render one band
            per outcome (a prop stat can be 15 rungs), so the filters still do
            something and "switching density preserves all available actions"
            requires they survive. The spread/total chip strip still hides
            them — it shows a single selected line, where the filters have
            nothing to act on. */}
        {/* The compact strip's held line, in this row's free width (a
            zero-basis slot: it never wraps the chips or grows the row). */}
        {showCompactLines && <LineHoldStatus entries={compactLineStatus} />}
        {/* UFC Fight Props (#848): markets that name this bout but whose
            identity is not exact are withheld, and say so — never rendered
            as rows. Zero-basis slot like the line above: it cannot wrap the
            chips or grow the row. */}
        {isUfc && effectiveTab === 'events' && ufcProps && ufcProps.withheld.length > 0 && (
          <span
            role="status"
            data-testid="ufc-fight-props-withheld"
            className="min-w-0 flex-1 basis-0 truncate text-xs text-amber-500/80"
            title={ufcProps.withheld.join('\n')}
          >
            {ufcProps.withheld.length} fight prop{ufcProps.withheld.length === 1 ? '' : 's'} withheld: bout or outcome identity not exact
          </span>
        )}
        {!showCompactLines && (
          <div className="ml-auto">
            <ChipGroup>
              <Chip active={hideNoVol} onClick={() => setHideNoVol((v) => !v)}>
                Hide 0-Vol
              </Chip>
              <Chip active={hideSettled} onClick={() => setHideSettled((v) => !v)}>
                Hide settled
              </Chip>
            </ChipGroup>
          </div>
        )}
      </div>

      {/* Same table as the moneyline, minus the Time column. The Odds column
          stays: it's where the expanded ladder drops your resting orders. With
          no 1H consensus it just reads "—" in the summary rows (as moneyline
          does whenever consensus is absent). */}
      {showCompactBands ? (
        <CompactOutcomeBands
          outcomes={bandOutcomes}
          marketSide={marketSide}
          priceMode={priceMode}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
          expandedKey={expandedBandKey}
          onToggleExpand={(key) => setExpandedBandKey((prev) => (prev === key ? null : key))}
          onSummaryYesSelect={(outcome) => { setMarketSide('yes'); setExpandedBandKey(outcome.key); }}
          onSummaryNoSelect={(outcome) => { setMarketSide('no'); setExpandedBandKey(outcome.key); }}
          onTrade={handleBandTrade}
          makerRebatePct={bandRebateFraction}
          emptyMessage={
            // Distinguish "the feed has nothing" from "your own filters hid
            // it" — the detailed table draws the same distinction, and a bare
            // "nothing here" would read as broken while filters are on.
            rows.length === 0
              ? 'No open markets yet.'
              : 'All markets hidden by the 0-Vol / settled filters.'
          }
        />
      ) : showCompactLines && event ? (
        <CompactLinesTable
          onInspectSide={(kind, side) => { holdShownCompactLine(kind); setMarketSide(side); setCompactExpandedRow(kind); }}
          opPeriod={effectivePeriod}
          showUsColumn={showUsLines}
          onUsPillTrade={(kind, side, cents) => {
            const idx = kind === 'spread' ? activeCompactSpreadIdx : activeCompactTotalIdx;
            const line = kind === 'spread' ? event.spreads[idx] : event.totals[idx];
            if (!line) return;
            holdShownCompactLine(kind);
            setMarketSide(side);
            onPanelLineChange?.({ kind, key: kind === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine) });
            disarmIfArmed(DISARM_REASON.usPrice);
            seedUsTicket({ game: kind === 'spread' ? synthesizeFromSpread(event, line as SpreadLine) : synthesizeFromTotal(event, line as TotalLine),
              teamSide: 'away', venue: 'polymarket', postOnly: false, initialOrderSide: side, prefillPrice: cents });
          }}
          event={event}
          marketSide={marketSide}
          priceMode={priceMode}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
          openOrders={openOrders}
          spreadIdx={activeCompactSpreadIdx}
          totalIdx={activeCompactTotalIdx}
          expandedRow={compactExpandedRow}
          onSelectSpreadIdx={(idx) => {
            setCompactSpreadIdx(idx);
            const line = event.spreads[idx];
            if (line) setCompactSpreadKey(spreadLineKey(line));
            setCompactExpandedRow('spread');
            if (line) onPanelLineChange?.({ kind: 'spread', key: spreadLineKey(line) });
          }}
          onSelectTotalIdx={(idx) => {
            setCompactTotalIdx(idx);
            const line = event.totals[idx];
            if (line) setCompactTotalKey(totalLineKey(line));
            setCompactExpandedRow('total');
            if (line) onPanelLineChange?.({ kind: 'total', key: totalLineKey(line) });
          }}
          onToggleExpand={(rowKind) => {
            if (compactExpandedRow !== rowKind) holdShownCompactLine(rowKind);
            setCompactExpandedRow((prev) => (prev === rowKind ? null : rowKind));
          }}
          lt2CardScope={
            lt2PeriodFor(effectivePeriod) != null
              ? {
                  surface: 'gd-game-lines',
                  period: lt2PeriodFor(effectivePeriod)!,
                  keyPrefix: `gl:${effectivePeriod}`,
                }
              : undefined
          }
          onPillTrade={(rowKind, venue, side, cents, meta) => {
            // Mirror the detailed table's trade wiring: flip YES/NO, point the
            // right-side panel at the clicked line (via onPanelLineChange ->
            // the emit effect) so it routes to the right market, and seed the
            // ticket from that line's synth. Branch per kind so the SpreadLine
            // / TotalLine element type narrows for the synth builder.
            if (side !== marketSide) setMarketSide(side);
            // A click on the row's own book holds the line it was showing (H-4).
            if (meta?.lineIdx == null) holdShownCompactLine(rowKind);
            // A multi-line block click names its OWN line via meta.lineIdx.
            const clickedIdx =
              meta?.lineIdx ??
              (rowKind === 'spread' ? activeCompactSpreadIdx : activeCompactTotalIdx);
            if (meta?.lineIdx != null) {
              if (rowKind === 'spread') {
                setCompactSpreadIdx(meta.lineIdx);
                const next = event.spreads[meta.lineIdx];
                if (next) setCompactSpreadKey(spreadLineKey(next));
              } else {
                setCompactTotalIdx(meta.lineIdx);
                const next = event.totals[meta.lineIdx];
                if (next) setCompactTotalKey(totalLineKey(next));
              }
            }
            const line =
              rowKind === 'spread' ? event.spreads[clickedIdx] : event.totals[clickedIdx];
            if (!line) return;
            const synth =
              rowKind === 'spread'
                ? synthesizeFromSpread(event, line as SpreadLine)
                : synthesizeFromTotal(event, line as TotalLine);
            const ctx: TradeTicketContext = {
              game: synth,
              teamSide: 'away',
              venue,
              initialOrderSide: side,
              prefillPrice: cents,
              postOnly: priceMode === 'maker',
              meta,
            };
            // Panel/bet-line context follows the click BEFORE the intercept
            // (review finding, 2026-08-18): an intercepted neighbor-line
            // click must not leave the right-side panel driving the previous
            // line while the table + hotkey targets moved to the new one.
            onPanelLineChange?.({
              kind: rowKind,
              key: rowKind === 'spread'
                ? spreadLineKey(line as SpreadLine)
                : totalLineKey(line as TotalLine),
            });
            // D-020 interception — coverage boundary: only full-game and
            // 1st-half spread/total are one-click eligible; every other
            // period falls through to the ticket AND disarms (V1 boundary).
            if (
              lt2PeriodFor(effectivePeriod) != null &&
              tryOneClickFromTicketCtx({
                ctx,
                surface: 'gd-game-lines',
                period: lt2PeriodFor(effectivePeriod)!,
                priceMode,
              })
            ) {
              return;
            }
            if (useOneClickStore.getState().armed) useOneClickStore.getState().disarm('This market uses the order ticket. Re-arm to return to supported one-click markets.');
            seedTicket(ctx);
          }}
        />
      ) : (
      <EdgeFadeScroller className={GAME_DETAIL_TABLE_FADE}>
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-60">Bet</Th>
              {!hideOddsColumn ? (
                <Th className="w-16 text-center" title="Unabated consensus odds (full-game only) — also hosts your resting orders in the ladder">UNBTD</Th>
              ) : (
                <Th className="w-10 text-center" title="Your resting orders at each ladder price level">{''}</Th>
              )}
              {!hideOpColumn && (
                <Th
                  className="w-24 text-center"
                  title={`${opHeaderTooltip()} Period rows read their own period/line OP bucket (full-feed B); rows without one read the full-game moneyline.`}
                >
                  {OP_HEADER_LABEL}
                </Th>
              )}
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_KALSHI}>K Vol</Th>
              <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_KALSHI}>K Liq</Th>
              <Th className="w-16 text-right" title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}>K Q</Th>
              <Th className="w-16 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</Th>
              {!hidePolyColumns && (
                <>
                  <Th className="w-32 text-center">Poly</Th>
                  <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_POLY}>P Vol</Th>
                  <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_POLY}>P Liq</Th>
                  <Th className="w-16 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</Th>
                </>
              )}
              {showUsLines && <><Th className="w-32 text-center">US</Th><UsMetricHeaders /></>}
            </tr>
          </thead>
          <tbody>
            {/* Map over the FULL rows (keeps i = line index for the panel
                context); skip no-volume rows when "Hide 0-Vol" is on, and
                settled rows when "Hide settled" is on (otherwise they render
                dimmed with a SETTLED chip). */}
            {/* Props/Events empty state — the tab is reachable with rows only,
                but the visibility toggles can drain it; say so instead of
                rendering a bare header. (Lines tab keeps its existing silent-
                empty behavior — UNCHANGED.) */}
            {effectiveTab !== 'lines' && visibleRows.every((r) => !r.visible) && (
              <tr>
                <td colSpan={(hideOpColumn ? 11 : 12) - (hidePolyColumns ? 4 : 0) + (showUsLines ? 4 : 0)} className="text-muted-foreground px-4 py-6 text-center text-sm">
                  {rows.length === 0
                    ? 'No open markets yet.'
                    : 'All markets hidden by the 0-Vol / settled filters.'}
                </td>
              </tr>
            )}
            {/* Compact Props / Events — one row per value ladder, thresholds
                on the strip; non-ladder rows (YRFI, extra innings) render as
                themselves. Its own map rather than a filtered `visibleRows`
                pass: a collapsed row stands for a whole ladder, so it needs the
                LADDER's key for React identity + expand, and the SELECTED
                rung's synth for trades. Columns, orderbook expand, POS chips
                and the filters are all the detailed path's. */}
            {showCompactLadders
              ? ladderTableRows.map((lad, i) => {
                  const side: TeamSide = lad.target.teamSide;
                  return (
                    <GameRows
                      key={lad.key}
                      game={lad.displaySynth}
                      gameIndex={i}
                      settled={lad.settled}
                      marketSide={marketSide}
                      priceMode={priceMode}
                      // Expansion is tracked by the compact key (the ladder's,
                      // for a collapsed row) — the same state the deep-link
                      // seeds, so a linked rung opens its book here too.
                      expandedTeamSide={expandedBandKey === lad.key ? side : null}
                      onToggleExpand={(key) => {
                        // #149: opening a PROP row is a market selection — seed
                        // the panel with the selected rung, exactly as its pill
                        // or rung strip would. Re-clicking the open row only
                        // collapses (the seed is null): the panel remounts on
                        // game.key, so a reseed would wipe in-progress entry.
                        const seed =
                          effectiveTab === 'props'
                            ? propRowSeedOnToggle({
                                prevOpenKey: expandedBandKey,
                                rowKey: key,
                                target: lad.target,
                                openTicket: openBandTicket,
                                marketSide,
                              })
                            : null;
                        setExpandedBandKey((prev) => (prev === key ? null : key));
                        if (!seed) return;
                        // Same claim/release as handleLadderTrade: drop any
                        // stale spread/total line, seed through onPillClick
                        // (which releases the middle table), then record the
                        // ladder as owning the panel so it renders the
                        // threshold strip and stays on live data.
                        onPanelLineChange?.(null);
                        seedTicket(seed.ctx);
                        setOpenBandTicket({ key, venue: seed.venue });
                        setPanelLadderKey(key);
                      }}
                      onTradeClick={(_key, tradeSide, venue, opts) =>
                        handleLadderTrade(lad.key, lad.target, tradeSide, venue, opts)
                      }
                      positionByTicker={positionByTicker}
                      ordersByTicker={ordersByTicker}
                      hideDateColumn
                      hideScoreColumn
                      hideOpColumn={hideOpColumn}
                      hidePolyColumns={hidePolyColumns}
                      holdColumnsOnExpand={hidePolyColumns}
                      hideOddsColumn={hideOddsColumn}
                      singleSide={side}
                      // The SELECTED rung's OP bucket (team-total join) — it
                      // moves with the strip because the target does
                      // (Danielle R1 DNL-001: a collapsed ladder rendered
                      // without one left the OP cell blank).
                      opBucket={lad.target.opBucket}
                      labelWidthPx={200}
                      wrapLabel={isLadderTab}
                      labelAccessory={
                        lad.chips && lad.selectedIdx != null ? (
                          <MiniLineStrip
                            chips={lad.chips}
                            selectedIdx={lad.selectedIdx}
                            onSelect={(idx) =>
                              handleSelectRung(lad.key, lad.chips![idx].key)
                            }
                          />
                        ) : undefined
                      }
                    />
                  );
                })
              : visibleRows.map((r, i) => {
              if (!r.visible) return null;
              // Grouped ladder rows (Props/Events) key zebra off their own
              // groupIndex; each rung is an independent bet, so the moneyline
              // pair/Tie group-selection logic never applies to them.
              const grouped = r.groupIndex != null;
              const isTie = !grouped && !!r.continuation;
              return (
              <GameRows
                key={r.synth.key}
                game={r.synth}
                showUsColumn={showUsLines}
                usLine={showUsLines && event ? effectiveBetType === 'spread'
                  ? usSpreadCell(event, event.spreads[i], usCandidates.spreads)
                  : usTotalCell(event, event.totals[i], usCandidates.totals) : undefined}
                onUsTradeClick={(_key, side, opts) => {
                  if (!showUsLines || !event) return;
                  const kind = effectiveBetType === 'spread' ? 'spread' : 'total';
                  const line = kind === 'spread' ? event.spreads[i] : event.totals[i];
                  if (!line) return;
                  onPanelLineChange?.({ kind, key: kind === 'spread' ? spreadLineKey(line as SpreadLine) : totalLineKey(line as TotalLine) });
                  if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                  disarmIfArmed(DISARM_REASON.usPrice);
                  seedUsTicket({ game: r.synth, teamSide: side, venue: 'polymarket', postOnly: false,
                    initialOrderSide: opts?.initialOrderSide, prefillPrice: opts?.priceCents });
                }}
                gameIndex={grouped ? r.groupIndex! : isTie ? Math.max(0, i - 1) : i}
                continuation={grouped ? groupedContinuation[i] : isTie && !!mlPair?.visible}
                groupSelected={grouped ? false : isTie ? mlPairExpanded : mlTieExpanded}
                groupExtendsBelow={grouped ? false : !isTie && !!mlTie?.visible}
                settled={r.settled}
                marketSide={marketSide}
                priceMode={priceMode}
                expandedTeamSide={expanded?.key === r.synth.key ? expanded.side : null}
                ladderLevels={5}
                dualOutcomeLadder={effectiveTab === 'lines' && (effectiveBetType === 'spread' || effectiveBetType === 'total')}
                lineReference={effectiveTab === 'lines' && !hideOpColumn ? lineLadderReference(event, effectiveBetType, effectiveBetType === 'spread' ? event?.spreads[i] : event?.totals[i], effectivePeriod) : undefined}
                onSummaryYesSelect={effectiveTab === 'lines' ? (key, side) => { setMarketSide('yes'); setExpanded({ key, side }); } : undefined}
                onSummaryNoSelect={effectiveTab === 'lines' ? (key, side) => { setMarketSide('no'); setExpanded({ key, side }); } : undefined}
                onToggleExpand={(key, side) => {
                  // #149: opening a PROP row seeds the panel with that exact
                  // rung, same as the Compact path. Re-clicking the open row
                  // only collapses (seed null — a reseed would remount the
                  // panel and wipe in-progress entry). Non-prop rows keep the
                  // expand-only behavior.
                  const seed =
                    effectiveTab === 'props'
                      ? propRowSeedOnToggle({
                          prevOpenKey: expanded?.side === side ? (expanded?.key ?? null) : null,
                          rowKey: key,
                          ticketKey: r.ladder?.groupKey,
                          target: { synth: r.synth, teamSide: side },
                          openTicket: openBandTicket,
                          marketSide,
                        })
                      : null;
                  setExpanded((e) => (e?.key === key && e?.side === side ? null : { key, side }));
                  if (!seed) return;
                  const ticketKey = r.ladder?.groupKey ?? key;
                  // Point the ladder's sticky selection at THIS rung, so the
                  // panel's threshold strip (and Compact, after a view flip)
                  // agrees with the seeded ticket.
                  if (r.ladder) {
                    setRungKeyByGroup((prev) => new Map(prev).set(ticketKey, seed.chipKey));
                  }
                  onPanelLineChange?.(null);
                  seedTicket(seed.ctx);
                  setOpenBandTicket({ key: ticketKey, venue: seed.venue });
                  setPanelLadderKey(r.ladder ? ticketKey : null);
                }}
                onTradeClick={(_key, side, venue, opts) => {
                  // Clicking a YES/NO pill flips the whole table (and the toolbar
                  // toggle) to that side — marketSide drives every GameRows here.
                  if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                  const ctx: TradeTicketContext = {
                    game: r.synth,
                    teamSide: side,
                    venue,
                    prefillPrice: opts?.priceCents,
                    initialOrderSide: opts?.initialOrderSide,
                    // GameRows' ladder derives postOnly itself (bid = join);
                    // no meta, so the intercept honors this value.
                    postOnly: opts?.postOnly ?? false,
                  };
                  // D-020 interception — spread/total lines on full-game/1H
                  // only (the synth's own kind gates non-line rows inside).
                  if (
                    lt2PeriodFor(effectivePeriod) != null &&
                    tryOneClickFromTicketCtx({
                      ctx,
                      surface: 'gd-game-lines',
                      period: lt2PeriodFor(effectivePeriod)!,
                      priceMode,
                    })
                  ) {
                    return;
                  }
                  if (useOneClickStore.getState().armed && opts?.priceCents != null) {
                    useOneClickStore.getState().disarm('This market uses the order ticket. Re-arm to return to supported one-click markets.');
                  }
                  // Drive the panel's bet-line context: spread/total rows map 1:1
                  // to the period's line array (row i = line i), so the panel gets
                  // the "Over X" / "wins by over X" layout. Moneyline → null.
                  const panelLine = effectiveBetType === 'spread'
                    ? event?.spreads[i]
                    : effectiveBetType === 'total'
                      ? event?.totals[i]
                      : null;
                  onPanelLineChange?.(
                    effectiveTab === 'lines' && panelLine
                      ? {
                          kind: effectiveBetType as 'spread' | 'total',
                          key: effectiveBetType === 'spread'
                            ? spreadLineKey(panelLine as SpreadLine)
                            : totalLineKey(panelLine as TotalLine),
                        }
                      : null,
                  );
                  seedTicket(ctx);
                  // #149: claim ladder ownership from the Detailed pill too —
                  // previously nothing here claimed or released it, so a stale
                  // compact-ladder context could keep driving the panel header
                  // after a Compact → Detailed flip. Props rows claim their
                  // ladder (panel gains the threshold strip, Compact parity);
                  // other ladder-tab rows release it so the panel goes plain.
                  if (effectiveTab === 'props' && r.ladder) {
                    const kalshiBook =
                      (side === 'away' ? r.synth.kalshi?.away : r.synth.kalshi?.home) ?? null;
                    const polyBook =
                      (side === 'away' ? r.synth.polymarket?.away : r.synth.polymarket?.home) ?? null;
                    const chipKey = chipKeyFor(kalshiBook, polyBook, `${r.synth.key}|${side}`);
                    const groupKey = r.ladder.groupKey;
                    setRungKeyByGroup((prev) => new Map(prev).set(groupKey, chipKey));
                    setOpenBandTicket({ key: groupKey, venue });
                    setPanelLadderKey(groupKey);
                    // #183: a prop pill also OPENS the matching inline order book —
                    // parity with the row/player-name action, against the same exact
                    // rung (r.synth.key + this pill's side). A pure set, never the
                    // row toggle: re-clicking the pill keeps the book open rather
                    // than closing it, so the pill can't desync the row it seeds.
                    setExpanded({ key: r.synth.key, side });
                  } else if (isLadderTab) {
                    setPanelLadderKey(null);
                  }
                }}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                hideDateColumn
                hideScoreColumn
                hideOpColumn={hideOpColumn}
                hidePolyColumns={hidePolyColumns}
                holdColumnsOnExpand={hidePolyColumns}
                singleSide={r.singleSide}
                // #651 — spread/total detailed rows show the SAME markets as the
                // compact chip-strip table, which carries the side tint; the other
                // bet types on this tab (moneyline, BTTS, odd/even) keep the
                // untinted treatment they share with the rest of the app.
                sideTint={effectiveBetType === 'spread' || effectiveBetType === 'total'}
                consensusKey={r.consensusKey}
                oddsOutcome={r.oddsOutcome}
                opBucket={r.opBucket}
                labelWidthPx={200}
                wrapLabel={isLadderTab}
              />
              );
            })}
          </tbody>
        </table>
      </EdgeFadeScroller>
      )}
      {isFootball && effectiveTab === 'events' && <UsGameProps key={game.key} game={game} catalogue={usGameProps}
        onSelect={ctx => { onPanelLineChange?.(null); onActiveLineChange?.(null); onPillClick(ctx); }} />}
      {effectiveTab === 'props' && isUsPlayerSport && <UsPlayerProps key={game.key} game={game} catalogue={usPlayers}
        onSelect={ctx => {
          onPanelLineChange?.(null);
          onActiveLineChange?.(null);
          onPillClick(ctx);
        }} />}
      {effectiveTab === 'lines' && (
        <OpSpecialsBlock gameKey={game.key} />
      )}
    </section>
  );
}
